/* START OF COMPILED CODE */

import Phaser from 'phaser';
import PlayerCharacter from '../components/player/PlayerCharacter';
import { initInventoryManager, inventoryManager } from "../managers/InventoryManager";
import { itemManager } from '../managers/ItemManager';
import { careManager } from '../managers/CareManager';
import { getStatsManager } from '../managers/StatsManager';
import { createChestAnimations, createPlotSignAnimations, createGenericAnimations } from '../util/Animations';
import ToolUI from '../components/ui/ToolUI';
import ToolSelectionUI from '../components/ui/ToolSelectionUI';
import SeedbagUI from '../components/ui/SeedbagUI';
import { DialogueWindow } from '../components/ui/DialoguePanel';
import Win98Window from '../components/ui/Windows98Window';


export default class FarmScene extends Phaser.Scene {
    constructor() {
        super({ key: 'Farm' });
        this.debugMode = false; // Debug mode flag
        
        // Farm feature sprites
        this.farmPlots = [];
        this.plotSigns = [];
        this.dungeonFence = null;
        this.chickenCoop = null;
        
        // Debug grid and farmable tile highlighting
        this.debugGrid = null;
        this.farmableTileHighlight = null;
        this.lastHighlightedTile = null; // Track last highlighted tile to avoid spam logging
        
        // Tile state management
        this.tileStates = new Map(); // Stores tile states: key = "plot_row_tile", value = {phase, ...cropData}
        this.rawTileSprites = []; // Stores raw tile sprites for cleanup (depth 2.5)
        this.wateredTileSprites = []; // Stores watered tile sprites for cleanup (depth 2.55)
        this.plantedSeedSprites = []; // Stores planted seed sprites for cleanup (depth 2.7)
        this.deadCropSprites = []; // Stores dead crop sprites for cleanup (depth 2.7)
        this.produceItems = []; // Stores harvested produce items awaiting pickup (depth 2.8)
        this.sparkleSprites = []; // Stores sparkle animations for harvestable crops (depth 2.9)
        this.debugMutantCrops = new Set(); // Tracks debug-converted mutant crops for testing
        
        // Growth progression tracking
        this.growthTimers = new Map(); // Stores active growth timers: key = "plot_row_tile", value = timerId
        this.GROWTH_PHASE_DURATION = 30 * 1000; // 30 seconds per phase
        this.HARVEST_DEADLINE = 15 * 60 * 1000;
        this.SEED_DEATH_TIMER = 5 * 60 * 1000; // 5 minutes to water seeds before they die
        this.TOTAL_GROWTH_TIME = 5 * this.GROWTH_PHASE_DURATION; // 2.5 minutes total growth time
        this.TOTAL_CROP_LIFETIME = this.SEED_DEATH_TIMER + this.TOTAL_GROWTH_TIME + this.HARVEST_DEADLINE; // 22.5 minutes total
        
        // Sprite depth layering:
        // 2.5 - Raw tile sprites (farm-tile-raw.png) - 16x16, aligned with farmable tiles
        // 2.55 - Watered tile sprites (farm-tile-watered.png) - 16x16, shows under crops
        // 2.7 - Crop sprites (32px tall, positioned 8px up so bottom aligns with tiles):
        //       - Planted seeds (frame 2) for phase 2
        //       - Growth stages (frames 3-7) for phases 3-7  
        //       - Dead crops (frame 10) for phase 10
        // 2.8 - Produce items (harvested crops awaiting pickup)
        
        // Phase System (0-indexed, aligned with Phaser frames):
        // Phase 1: Tilled soil (stored in DB, no sprite - shows farmplot background)
        // Phase 2: Seeds planted (frame 2) - maps to sprite frame 2
        // Phase 3-7: Growth stages (frames 3-7) - direct 1:1 mapping  
        // Phase 7: Ready for harvest
        // Phase 10: Dead crops (frame 10) - shows withered plants
        
        // Tool animation system
        this.currentToolSprite = null;
        
        // Scene transition flag
        this.isTransitioning = false;
        
        // Plot numbering: clockwise starting from top left
        // Array index → Clockwise plot number
        this.plotClockwiseMapping = [1, 2, 4, 3]; // [top-left, top-right, bottom-left, bottom-right] → [1, 2, 4, 3]
        
        // Audio objects
        this.sounds = {
            farm_ambiance: null
        };
        
        // User unlock states (will be synced with database)
        this.unlockStates = {
            beach_unlocked: false,
            farmplot_1_unlocked: false,
            farmplot_2_unlocked: false,
            farmplot_3_unlocked: false,
            farmplot_4_unlocked: false,
            chicken_coop_unlocked: false,
            dungeon_unlocked: false
        };
        
        // Movement sounds
        this.lastStepTime = 0;
        this.stepSounds = ['footsteps1', 'footsteps2', 'footsteps3', 'footsteps4', 'footsteps5'];
        
        // Initialize inventory manager
        initInventoryManager(this);
        
        // Initialize stats manager
        this.statsManager = getStatsManager();
        
        // Previous position and time for movement tracking
        this.previousPosition = { x: 0, y: 0 };
        this.lastDialogueTime = 0;
        this.MOVEMENT_CHECK_INTERVAL = 20000; // Check every 20 seconds
        
        // Dialogue cooldown to prevent spam
        this.DIALOGUE_COOLDOWN = 30000; // 30 seconds between mood dialogues
    }

    // Assets are pre-loaded by LoadingScene, no need to load here
    preload() {
        // All assets are handled by LoadingScene
    }

    async create(data = {}) {
        //console.log('🎬 FarmScene.create() called with data:', data);

        // Reset scene transition flag
        this.isTransitioning = false;

        // Setup camera viewport FIRST to constrain fade effects
        this.cameras.main.setViewport(0, 0, 256, 192);
        
        // Disable browser context menu for right-click functionality
        this.input.mouse.disableContextMenu();
        
        // Start with camera fully faded out while we prepare the scene
        this.cameras.main.fadeOut(0, 0, 0, 0);
        
        initInventoryManager(this);
        
        // Initialize stats manager
        this.statsManager = getStatsManager();
        
        // Create player animations
        PlayerCharacter.createAnimations(this);
        
        // Create chest animations
        createChestAnimations(this);
        
        // Create plot sign animations
        createPlotSignAnimations(this);
        
        // Create generic animations (including sparkle)
        createGenericAnimations(this);
        
        // Get user's preferred color
        const playerColor = await PlayerCharacter.getUserColor(this);
        
        // Setup camera with larger world bounds for 512x384 map (viewport already set above)
        this.cameras.main.setBounds(0, 0, 512, 384);
        this.cameras.main.setZoom(1);
        
        // Load background with depth
        this.add.image(256, 192, 'farm_map').setDepth(0);
        this.add.image(256, 192, 'farm_fence').setDepth(10);
        
        // Setup collision map (invisible)
        this.collisionMap = this.add.image(256, 192, 'farm_collision').setDepth(1);
        this.collisionMap.setVisible(false);
        
        // Define spawn points for different entry locations
        // Add new spawn points here when connecting to other scenes
        // Format: sceneName: { x: positionX, y: positionY }
        const spawnPoints = {
            default: { x: 277, y: 176 }, // Default center spawn
            fromRiver: { x: 415, y: 192 }, // Spawn in open area, clear of center collision
            fromBeach: { x: 50, y: 192 }, // Spawn near beach transition (left side) 
            fromMainScene: { x: 277, y: 176 }, // Spawn near main scene transition (top)
        };
        
        // Determine spawn position based on data passed from previous scene
        const spawnKey = data.spawnPoint || 'default';
        const spawnPosition = spawnPoints[spawnKey] || spawnPoints.default;
        
        /* 
        console.log(`Farm scene spawning player at: ${spawnKey} (${spawnPosition.x}, ${spawnPosition.y})`);
        console.log('Scene data received:', data); 
        */
        
        // Setup player with correct color and spawn position
        this.player = new PlayerCharacter(this, spawnPosition.x, spawnPosition.y, playerColor);
        this.player.setDepth(5);
        this.player.playColorAnimation("idle2", true);
        
        // Initialize position tracking for mood dialogue dismissal
        this.lastPlayerPosition = { x: this.player.x, y: this.player.y };
        
        // Log actual player position after creation
        // console.log(`Player actually positioned at: (${this.player.x}, ${this.player.y})`);
        
        // Camera follows player
        this.cameras.main.startFollow(this.player);
        
        // Log camera position after following starts
        /* console.log(`Camera scroll position: (${this.cameras.main.scrollX}, ${this.cameras.main.scrollY})`);
        console.log(`Camera center would be at: (${this.cameras.main.scrollX + 128}, ${this.cameras.main.scrollY + 96})`);
         */
        
        // Setup audio
        this.setupAudio();
        
        // Sync unlock states with database
        await this.syncUnlockStates();
        
        // Setup farm features
        this.setupFarmPlots();
        this.setupPlotSigns();
        this.setupDungeonFence();
        this.setupChickenCoop();
        this.setupChest();
        
        // Initialize tile states and raw tile sprites
        await this.initializeTileStates();
        
        // Setup collision detection
        this.setupCollision();
        
        // Log player position after collision setup to see if it changed
        //console.log(`Player position after collision setup: (${this.player.x}, ${this.player.y})`);
        
        // Setup input handlers
        this.setupInputHandlers();
        
        // Setup debug mode toggle
        //this.setupDebugMode();
        
        // Initialize collision visuals
        this.updateCollisionVisuals();
        
        // Initialize Tool UI overlay
        this.toolUI = new ToolUI(this);
        
        // Initialize Tool Selection and Seedbag UI overlays
        this.toolSelectionUI = new ToolSelectionUI(this);
        this.seedbagUI = new SeedbagUI(this);
        
        // UI interaction blocking flag
        this.uiInteractionBlocked = false;
        
        // Add a delayed check to see if player position changes after setup
        this.time.delayedCall(100, () => {
           /*  
           console.log(`Player position after complete setup: (${this.player.x}, ${this.player.y})`);
            console.log(`Camera scroll after setup: (${this.cameras.main.scrollX}, ${this.cameras.main.scrollY})`);
            console.log(`Player hop offset: ${this.player.hopOffset}`);
            console.log(`Player display origin: (${this.player.displayOriginX}, ${this.player.displayOriginY})`);
             */

            // Calculate where the player should appear on screen
            const screenX = this.player.x - this.cameras.main.scrollX;
            const screenY = this.player.y - this.cameras.main.scrollY;

            // console.log(`Player should appear on screen at: (${screenX}, ${screenY})`);
        });
        
        // Add debug key to check positioning anytime
        this.input.keyboard.on('keydown-P', () => {
            //this.debugPlayerPosition();
        });
        
        // Add debug key to test mutant crop system (M key)
        this.input.keyboard.on('keydown-M', () => {
            this.debugConvertToMutantCrop();
        });
        
        // Add debug key to force pickup all stuck items (U key)
        this.input.keyboard.on('keydown-U', () => {
            this.debugForcePickupAll();
        });
        
        // Set up scene shutdown event to ensure cleanup
        this.events.once('shutdown', () => {
            //console.log('🧹 FarmScene shutdown event - calling cleanup...');
            this.cleanup();
        });
        
        // Scene is fully prepared - now fade in to show the user
        this.cameras.main.fadeIn(800, 0, 0, 0);
        //console.log('Farm scene fully loaded and ready - fading in');
    }
    
    debugPlayerPosition() {
        // Calculate where the player should appear on screen
        const screenX = this.player.x - this.cameras.main.scrollX;
        const screenY = this.player.y - this.cameras.main.scrollY;
    }
    
    debugConvertToMutantCrop() {
        //console.log('🧪 DEBUG: Looking for harvestable crops to convert to mutant...');
        
        // Find all tiles with harvestable crops (phase 7)
        let converted = false;
        
        for (const [key, tileState] of this.tileStates.entries()) {
            if (tileState.phase === 7 && !converted) {
                // Parse the tile key to get coordinates
                const [plotIndex, farmableRow, tileIndex] = key.split('_').map(Number);
                
                //console.log(`🔥 DEBUG: Converting crop at Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex} to MUTANT!`);
                
                // Add to debug mutant tracking
                this.debugMutantCrops.add(key);
                
                // Remove existing sparkle animation
                this.removeSparkleAnimation(plotIndex, farmableRow, tileIndex);
                
                // Add mutant sparkle animation
                this.addMutantSparkleAnimation(plotIndex, farmableRow, tileIndex);
                
                // Mark as converted
                converted = true;
                
                break;
            }
        }
        
        if (!converted) {
            //console.log('❌ DEBUG: No harvestable crops found (need crops at phase 7). Grow some crops first!');
        }
    }
    
    debugForcePickupAll() {
        //console.log('🆘 DEBUG: Force picking up all remaining produce items...');
        
        const unpickedItems = this.produceItems.filter(p => !p.picked);
        //console.log(`Found ${unpickedItems.length} unpicked items to force pickup`);
        
        // Force pickup all items (from end to start to avoid index issues)
        for (let i = this.produceItems.length - 1; i >= 0; i--) {
            const produce = this.produceItems[i];
            if (!produce.picked) {
                //console.log(`🆘 Force picking up: ${produce.rewardType || 'crop'} ${produce.rewardId || produce.cropId}`);
                this.pickupProduce(produce, i);
            }
        }
        
        //console.log(`🆘 DEBUG: Force pickup complete. ${this.produceItems.length} items remaining.`);
    }

    async syncUnlockStates() {
        try {
            const gameService = window.gameService;
            if (!gameService) {
                console.warn('GameService not available, using default locked state');
                return;
            }
            
            // Fetch user options from backend
            const userOptions = await gameService.getUserOptions();
            
            // Update local unlock states
            this.unlockStates.beach_unlocked = userOptions.beach_unlocked || false;
            this.unlockStates.farmplot_1_unlocked = userOptions.farmplot_1_unlocked || false;
            this.unlockStates.farmplot_2_unlocked = userOptions.farmplot_2_unlocked || false;
            this.unlockStates.farmplot_3_unlocked = userOptions.farmplot_3_unlocked || false;
            this.unlockStates.farmplot_4_unlocked = userOptions.farmplot_4_unlocked || false;
            this.unlockStates.chicken_coop_unlocked = userOptions.chicken_coop_unlocked || false;
            this.unlockStates.dungeon_unlocked = userOptions.dungeon_unlocked || false;
        } catch (error) {
            console.error('Error syncing unlock states:', error);
        }
    }

    setupAudio() {
        // Stop any existing farm ambiance
        if (this.sound.get('farm')) {
            this.sound.get('farm').stop();
        }
        if (this.sound.get('bgm4')) {
            this.sound.get('bgm4').stop();
        }
        if (this.sound.get('bgm5')) {
            this.sound.get('bgm5').stop();
        }
        if (this.sound.get('bgm6')) {
            this.sound.get('bgm6').stop();
        }
        
        // Check if the audio assets exists
        if (!this.cache.audio.exists('farm')) {
            console.warn('Farm audio not found in cache');
            return;
        }
        if (!this.cache.audio.exists('bgm4')) {
            console.warn('bgm4 audio not found in cache');
            return;
        }
        if (!this.cache.audio.exists('bgm5')) {
            console.warn('bgm5 audio not found in cache');
            return;
        }
        if (!this.cache.audio.exists('bgm6')) {
            console.warn('bgm6 audio not found in cache');
            return;
        }

        
        // Initialize farm ambiance using the 'farm' key loaded in LoadingScene
        this.sounds.farm_ambiance = this.sound.add('farm', { volume: 0.3, loop: true  });
        this.sounds.bgm4 = this.sound.add('bgm4', { volume: 0.1, loop: true });
        this.sounds.bgm5 = this.sound.add('bgm5', { volume: 0.1, loop: true });
        this.sounds.bgm6 = this.sound.add('bgm6', { volume: 0.1, loop: true });
        
        // Start ambiance with loop
        this.sounds.farm_ambiance.play();   
        this.sounds.bgm4.play();
    }

    setupFarmPlots() {
        // Define farmplot positions (you can adjust these as needed)
        const farmplotPositions = [
            { x: 176, y: 233 }, // Farmplot 1
            { x: 368, y: 233 }, // Farmplot 2
            { x: 176, y: 329 }, // Farmplot 3
            { x: 368, y: 329 }  // Farmplot 4
        ];
        
        for (let i = 0; i < 4; i++) {
            const plotIndex = i + 1;
            const position = farmplotPositions[i];
            const isUnlocked = this.unlockStates[`farmplot_${plotIndex}_unlocked`];
            
            const farmplot = this.add.image(
                position.x, 
                position.y, 
                isUnlocked ? 'farmplot_unlocked' : 'farmplot_locked'
            ).setDepth(2);
            
            // Store reference
            this.farmPlots.push({
                sprite: farmplot,
                index: plotIndex,
                unlocked: isUnlocked,
                position: position
            });
        }
    }
    
    setupPlotSigns() {
        // Use the same positions as farm plots for the plot signs
        const plotSignPositions = [
            { x: 176, y: 233 }, // Plot sign 1 (center of farmplot 1)
            { x: 368, y: 233 }, // Plot sign 2 (center of farmplot 2)
            { x: 176, y: 329 }, // Plot sign 3 (center of farmplot 3)
            { x: 368, y: 329 }  // Plot sign 4 (center of farmplot 4)
        ];
        
        for (let i = 0; i < 4; i++) {
            const plotIndex = i + 1;
            const position = plotSignPositions[i];
            const isUnlocked = this.unlockStates[`farmplot_${plotIndex}_unlocked`];
            
            // Only show plot signs for locked plots (they're purchase/unlock prompts)
            if (!isUnlocked) {
                // Create plot sign sprite
                const plotSign = this.add.sprite(position.x, position.y, 'plot_sign', 0);
                plotSign.setDepth(6); // Above player (depth 5) for better visibility
                plotSign.setOrigin(0.5, 0.5); // Center origin
                
                // Start the idle animation
                plotSign.play('plot_sign_idle');
                
                // Create collision area at the bottom of the sign for depth simulation
                // 16x16 area positioned at the bottom of the 16x24 sign
                const collisionZone = this.add.zone(position.x, position.y + 8, 16, 16);
                this.physics.world.enable(collisionZone);
                collisionZone.body.setImmovable(true);
                
                // Create interaction zone for dialogue (larger area around the sign)
                const interactionZone = this.add.zone(position.x, position.y, 40, 40);
                this.physics.world.enable(interactionZone);
                interactionZone.body.setImmovable(true);
                
                // Store plot index in zones for interaction handling
                collisionZone.plotIndex = plotIndex;
                interactionZone.plotIndex = plotIndex;
                
                // Store reference
                this.plotSigns.push({
                    sprite: plotSign,
                    collisionZone: collisionZone,
                    interactionZone: interactionZone,
                    index: plotIndex,
                    position: position,
                    unlocked: isUnlocked,
                    playerInZone: false
                });
                
                // Set up collision detection with player for depth simulation
                this.physics.add.collider(this.player, collisionZone);
                
                // Set up overlap detection for plot sign interaction
                this.physics.add.overlap(this.player, interactionZone, () => {
                    this.handlePlotSignApproach(plotIndex);
                });
                
                //console.log(`Plot sign ${plotIndex} created at (${position.x}, ${position.y}) - LOCKED`);
            } else {
                //console.log(`Plot sign ${plotIndex} not shown - plot is UNLOCKED`);
            }
        }
    }

    setupDungeonFence() {
        // Position the dungeon fence (adjust as needed)
        if (!this.unlockStates.dungeon_unlocked) {
            this.dungeonFence = this.add.image(5, 126, 'dungeonfence').setDepth(2);
        }
    }

    setupChickenCoop() {
        // Position the chicken coop in top right corner
        const isUnlocked = this.unlockStates.chicken_coop_unlocked;
        this.chickenCoop = this.add.image(480, 40, isUnlocked ? 'coop_unlocked' : 'coop_locked').setDepth(2);
    }

    async initializeTileStates() {
        try {
            // Get the game service and load tile states from database
            const gameService = window.gameService;
            if (!gameService) {
                console.warn('GameService not available, using default tile states');
                this.createDefaultRawTiles();
                return;
            }

            // Sync crop states with proper offline progression
            await this.syncCropState();
            
            // Fill in missing tiles with default raw state
            this.fillMissingRawTiles();
            
            // Check for expired crops and tiles FIRST to clean up database/state
            await this.checkExpiredCropsAndTiles();
            
            // Now create sprites based on synced tile states
            // Create raw tile sprites for all untilled farmable tiles
            this.createRawTileSprites();
            
            // Create watered tile sprites for all watered farmable tiles
            this.createWateredTileSprites();
            
            // Create planted seed sprites for all planted farmable tiles
            this.createPlantedSeedSprites();
            
            // Create dead crop sprites for all dead farmable tiles
            this.createDeadCropSprites();
            
            // Create sparkle animations for existing harvestable crops
            this.createSparkleAnimations();
            
            // Restart growth timers for crops that are still growing
            this.restartGrowthTimers();
            
        } catch (error) {
            console.error('Error loading tile states:', error);
            this.createDefaultRawTiles();
        }
    }

    async syncCropState() {
        console.log('Starting crop state synchronization...');
        
        // Load crop data from backend
        const gameService = window.gameService;
        const cropsData = await gameService.getUserCrops();
        //console.log('Raw crop data loaded:', cropsData);
        
        // Calculate offline progression for each crop
        const currentTime = Date.now();
        const updatedCrops = [];
        
        for (const crop of cropsData) {
            const originalPhase = crop.current_phase;
            let newPhase = originalPhase;
            let shouldSync = false;
            
            // Only process crops that can grow (phases 2-7)
            if (crop.current_phase >= 2 && crop.current_phase < 7 && crop.times_watered > 0) {
                // Calculate time since last phase advancement
                const lastPhaseTime = new Date(crop.time_since_last_phase).getTime();
                const timeSinceLastPhase = currentTime - lastPhaseTime;
                
                // Calculate how many phases should have passed (30 seconds per phase)
                const phasesPassed = Math.floor(timeSinceLastPhase / this.GROWTH_PHASE_DURATION);
                
                if (phasesPassed > 0) {
                    newPhase = Math.min(crop.current_phase + phasesPassed, 7);
                    shouldSync = true;
                    
                    //console.log(`📈 Crop offline progression: Plot ${crop.plot}, Row ${crop.row}, Tile ${crop.tile} - Phase ${originalPhase} → ${newPhase} (${phasesPassed} phases in ${Math.round(timeSinceLastPhase/1000)}s)`);
                }
            }
            
            // Check if mature crops (phase 7) should be withered
            if (crop.current_phase === 7 || newPhase === 7) {
                const lastPhaseTime = new Date(crop.time_since_last_phase).getTime();
                const timeAtMaturity = lastPhaseTime + ((7 - crop.current_phase) * this.GROWTH_PHASE_DURATION);
                const timeSinceMaturity = currentTime - timeAtMaturity;
                
                // If more than 15 minutes since maturity, wither the crop
                if (timeSinceMaturity > this.HARVEST_DEADLINE) {
                    newPhase = 10; // Withered
                    shouldSync = true;
                    
                    //console.log(`💀 Crop withered: Plot ${crop.plot}, Row ${crop.row}, Tile ${crop.tile} - Mature for ${Math.round(timeSinceMaturity/1000)}s (deadline: ${this.HARVEST_DEADLINE/1000}s)`);
                }
            }
            
            // If phase changed, update the database
            if (shouldSync && newPhase !== originalPhase) {
                try {
                    await gameService.advanceGrowth(crop.plot, crop.row, crop.tile, newPhase);
                    //console.log(`✅ Database synced: Plot ${crop.plot}, Row ${crop.row}, Tile ${crop.tile} updated to phase ${newPhase}`);
                    
                    // Update the crop data for local processing with proper timestamp
                    crop.current_phase = newPhase;
                    crop.time_since_last_phase = new Date(currentTime).toISOString();
                } catch (error) {
                    console.error(`❌ Failed to sync crop to database:`, error);
                }
            }
            
            updatedCrops.push(crop);
        }
        
        // Process the updated crop data into tile states
        this.processCropData(updatedCrops);
        
        //console.log(`🌱 Crop state synchronization complete. Processed ${updatedCrops.length} crops.`);
    }
    
    processCropData(cropsData) {
        // Clear existing states
        this.tileStates.clear();
        
        // Process each crop entry
        cropsData.forEach(crop => {
            const key = `${crop.plot}_${crop.row}_${crop.tile}`;
            const tileState = {
                phase: crop.current_phase,
                cropId: crop.crop_id,
                timesWatered: crop.times_watered,
                isReadyForHarvest: crop.is_ready_for_harvest,
                isWithered: crop.is_withered,
                plantedAt: crop.planted_at,
                lastPhaseAdvancement: new Date(crop.time_since_last_phase).getTime() // Convert to timestamp
            };
            
            // Add growthStarted for crops that have been watered (growth has begun)
            if (crop.current_phase >= 2 && crop.times_watered > 0) {
                tileState.growthStarted = new Date(crop.planted_at).getTime();
            }
            
            // Add timer fields based on phase
            if (crop.current_phase === 1) {
                // Tilled tiles revert to raw after 15 minutes if not planted
                const tillTime = crop.planted_at ? new Date(crop.planted_at).getTime() : Date.now();
                tileState.revertTime = tillTime + (15 * 60 * 1000);
            } else if (crop.current_phase === 2 && crop.times_watered === 0) {
                // Planted crops die after 5 minutes if not watered
                const plantTime = crop.planted_at ? new Date(crop.planted_at).getTime() : Date.now();
                tileState.deathTimer = plantTime + this.SEED_DEATH_TIMER;
            }
            
            this.tileStates.set(key, tileState);
        });
        
    }
    
    fillMissingRawTiles() {
        // Fill in any missing farmable positions with raw (phase 0) state
        this.farmPlots.forEach(plot => {
            if (!plot.unlocked) return;
            
            // Each plot has 3 farmable rows, 10 tiles each
            for (let row = 1; row <= 3; row++) {
                for (let tile = 1; tile <= 10; tile++) {
                    const key = `${plot.index}_${row}_${tile}`;
                    // Only add if not already in database
                    if (!this.tileStates.has(key)) {
                        this.tileStates.set(key, { phase: 0 }); // Phase 0 = raw/untilled
                    }
                }
            }
        });
        
        //console.log(`Total tile states after filling missing: ${this.tileStates.size}`);
    }
    
    createDefaultRawTiles() {
        // Create raw tiles for all farmable positions in unlocked plots
        this.farmPlots.forEach(plot => {
            if (!plot.unlocked) return;
            
            // Each plot has 3 farmable rows, 10 tiles each
            for (let row = 1; row <= 3; row++) {
                for (let tile = 1; tile <= 10; tile++) {
                    const key = `${plot.index}_${row}_${tile}`;
                    // Only add if not already in database
                    if (!this.tileStates.has(key)) {
                        this.tileStates.set(key, { phase: 0 }); // Phase 0 = raw/untilled
                    }
                }
            }
        });
        
        this.createRawTileSprites();
        this.createWateredTileSprites();
        this.createPlantedSeedSprites();
        this.createDeadCropSprites();
    }
    
    createRawTileSprites() {
        // Clear existing raw tile sprites
        this.rawTileSprites.forEach(sprite => sprite.destroy());
        this.rawTileSprites = [];
        
        // Count different phases for debugging
        let phase0Count = 0;
        let phase1Count = 0;
        let otherPhaseCount = 0;
        
        // Create raw tile sprites for phase 0 (untilled) tiles
        this.tileStates.forEach((tileData, key) => {
            if (tileData.phase === 0) {
                phase0Count++;
                const [plot, row, tile] = key.split('_').map(Number);
                const sprite = this.createRawTileSprite(plot, row, tile);
                if (sprite) {
                    this.rawTileSprites.push(sprite);
                }
            } else if (tileData.phase === 1) {
                phase1Count++;
            } else {
                otherPhaseCount++;
            }
        });
        
        //console.log(`Tile phases - Raw (0): ${phase0Count}, Tilled (1): ${phase1Count}, Other: ${otherPhaseCount}`);
        //console.log(`Created ${this.rawTileSprites.length} raw tile sprites`);
    }
    
    createWateredTileSprites() {
        // Clear existing watered tile sprites
        this.wateredTileSprites.forEach(sprite => sprite.destroy());
        this.wateredTileSprites = [];
        
        // Create watered tile sprites for tiles that are watered (have timesWatered > 0) and not dead
        this.tileStates.forEach((tileData, key) => {
            if (tileData.timesWatered && tileData.timesWatered > 0 && tileData.phase !== 10) {
                const [plot, row, tile] = key.split('_').map(Number);
                const sprite = this.createWateredTileSprite(plot, row, tile);
                if (sprite) {
                    this.wateredTileSprites.push(sprite);
                }
            }
        });
        
        //console.log(`Created ${this.wateredTileSprites.length} watered tile sprites`);
    }
    
    createWateredTileSprite(plotIndex, farmableRow, tileIndex) {
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot || !plot.unlocked) return null;
        
        // Calculate world position
        // farmableRow 1-3 maps to tileY 0, 2, 4
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1; // Convert 1-10 to 0-9
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        const worldY = plotTop + (tileY * 16) + 8;
        
        // Create watered tile sprite
        const sprite = this.add.image(worldX, worldY, 'farm_tile_watered');
        sprite.setDepth(2.55); // Above raw tiles (2.5) but below seed sprites (2.7)
        sprite.setOrigin(0.5, 0.5);
        
        return sprite;
    }
    
    createPlantedSeedSprites() {
        // Clear existing planted seed sprites
        this.plantedSeedSprites.forEach(sprite => sprite.destroy());
        this.plantedSeedSprites = [];
        
        // Count different phases for debugging
        let plantedCount = 0;
        let deadCount = 0;
        let tilledCount = 0;
        let rawCount = 0;
        
        // Create planted seed sprites for tiles that have living crops planted (phase >= 2 and cropId > 0)
        this.tileStates.forEach((tileData, key) => {
            const [plot, row, tile] = key.split('_').map(Number);
            
            if (tileData.phase === 10) {
                deadCount++;
                // Dead crops handled by createDeadCropSprites()
            } else if (tileData.phase === 0) {
                rawCount++;
                // Raw tiles handled by createRawTileSprites()
            } else if (tileData.phase === 1) {
                tilledCount++;
                // Tilled tiles show no additional sprite - just farmplot background
            } else if (tileData.phase >= 2 && tileData.cropId && tileData.cropId > 0) {
                plantedCount++;
                const sprite = this.createPlantedSeedSprite(plot, row, tile, tileData.cropId, tileData.phase);
                if (sprite) {
                    this.plantedSeedSprites.push(sprite);
                }
            }
        });
        
        //console.log(`Tile state summary - Raw: ${rawCount}, Tilled: ${tilledCount}, Planted: ${plantedCount}, Dead: ${deadCount}`);
        //console.log(`Created ${this.plantedSeedSprites.length} planted seed sprites`);
    }
    
    createPlantedSeedSprite(plotIndex, farmableRow, tileIndex, cropId, phase = 2) {
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot || !plot.unlocked) return null;
        
        // Get crop texture name from ItemManager
        // Crop IDs are stored as numbers but ItemManager expects "Consumable_X" format
        const cropItem = itemManager.getItemData(`Consumable_${cropId}`);
        if (!cropItem) {
            console.warn(`Crop item ${cropId} not found (looked for Consumable_${cropId})`);
            return null;
        }
        
        // Calculate world position
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        // Adjust Y position for 32px tall sprites - move up 8px so bottom aligns with tile
        const worldY = plotTop + (tileY * 16) + 8 - 8;
        
        // Determine frame based on growth phase (Phaser uses 0-indexed frames)
        // Phase 2 = Seeds (frame 2), Phase 3-7 = Growth stages (frames 3-7)
        let frame;
        if (phase === 2) {
            frame = 2; // Seeds (0-indexed, so frame 2 is the 3rd sprite)
        } else if (phase >= 3 && phase <= 7) {
            frame = phase; // Phase 3->frame 3, Phase 4->frame 4, etc.
        } else {
            frame = 2; // Default to seeds for unexpected phases
            console.warn(`Unexpected growth phase ${phase}, defaulting to seeds frame`);
        }
        
        // Create crop sprite using the correct frame
        const sprite = this.add.image(worldX, worldY, cropItem.textureKey, frame);
        sprite.setDepth(2.7); // Above watered tiles (2.6) but below player
        sprite.setOrigin(0.5, 0.5);
        
        return sprite;
    }
    
    createDeadCropSprites() {
        // Clear existing dead crop sprites
        this.deadCropSprites.forEach(sprite => sprite.destroy());
        this.deadCropSprites = [];
        
        // Create dead crop sprites for tiles that have dead crops (phase 10 and cropId > 0)
        this.tileStates.forEach((tileData, key) => {
            if (tileData.phase === 10 && tileData.cropId && tileData.cropId > 0) {
                const [plot, row, tile] = key.split('_').map(Number);
                const sprite = this.createDeadCropSprite(plot, row, tile, tileData.cropId);
                if (sprite) {
                    this.deadCropSprites.push(sprite);
                }
            }
        });
        
        //console.log(`Created ${this.deadCropSprites.length} dead crop sprites`);
    }
    
    createDeadCropSprite(plotIndex, farmableRow, tileIndex, cropId) {
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot || !plot.unlocked) return null;
        
        // Get crop texture name from ItemManager
        // Crop IDs are stored as numbers but ItemManager expects "Consumable_X" format
        const cropItem = itemManager.getItemData(`Consumable_${cropId}`);
        if (!cropItem) {
            console.warn(`Crop item ${cropId} not found for dead crop sprite (looked for Consumable_${cropId})`);
            return null;
        }
        
        // Calculate world position
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        // Adjust Y position for 32px tall sprites - move up 8px so bottom aligns with tile
        const worldY = plotTop + (tileY * 16) + 8 - 8;
        
        // Create dead crop sprite using frame 10 of the crop spritesheet (0-indexed)
        const sprite = this.add.image(worldX, worldY, cropItem.textureKey, 10);
        sprite.setDepth(2.7); // Same depth as planted seeds
        sprite.setOrigin(0.5, 0.5);
        
        return sprite;
    }
    
    createRawTileSprite(plotIndex, farmableRow, tileIndex) {
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot || !plot.unlocked) return null;
        
        // Calculate world position
        // farmableRow 1-3 maps to tileY 0, 2, 4
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1; // Convert 1-10 to 0-9
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        const worldY = plotTop + (tileY * 16) + 8;
        
        // Create raw tile sprite
        const sprite = this.add.image(worldX, worldY, 'farm_tile_raw');
        sprite.setDepth(2.5); // Above farmplot but below player
        sprite.setOrigin(0.5, 0.5);
        
        return sprite;
    }
    
    getTileKey(plotIndex, farmableRow, tileIndex) {
        return `${plotIndex}_${farmableRow}_${tileIndex}`;
    }
    
    getTileState(plotIndex, farmableRow, tileIndex) {
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        return this.tileStates.get(key) || { phase: 0 };
    }

    setupCollision() {
        //console.log('🔧 setupCollision() called - creating collision bodies');

        // Set world bounds for the larger map
        this.physics.world.setBounds(0, 0, 512, 384);
        this.player.body.setCollideWorldBounds(true);

        // Create simple rectangular collision areas instead of pixel-perfect collision
        this.collisionBodies = this.physics.add.staticGroup();
        //console.log('  Created collisionBodies group:', !!this.collisionBodies);
        
        // Define collision rectangles for walls and boundaries
        // To adjust: Change x, y (center position), width, height values
        // Press 'D' in-game to toggle debug mode and see collision rectangles
        const collisionAreas = [
            { x: 46, y: 24, width: 92, height: 48 },
            { x: 268, y: 72, width: 192, height: 132 },
            { x: 137, y: 72, width: 84, height: 132 },
            { x: 380, y: 64, width: 36, height: 24 },
            { x: 448, y: 8, width: 160, height: 16 },
            { x: 5, y: 50, width: 10, height: 100 },
            { x: 5, y: 220, width: 10, height: 150 },
            { x: 489, y: 352, width: 48, height: 68 },
            { x: 505, y: 264, width: 16, height: 240 },
            { x: 480, y: 48, width: 64, height: 44},
        ];
        
        // Create collision rectangles
        collisionAreas.forEach(area => {
            const rectangle = this.add.rectangle(area.x, area.y, area.width, area.height, 0xFF0000, 0);
            this.physics.add.existing(rectangle, true); // true makes it static
            this.collisionBodies.add(rectangle);
        });

        //console.log(`  Created ${collisionAreas.length} collision rectangles`);
        //console.log('  collisionBodies children count:', this.collisionBodies.children.size);

        // Set up collision between player and collision bodies
        this.physics.add.collider(this.player, this.collisionBodies);
        //console.log('  Set up collider between player and collision bodies');

        // Set up scene transition areas
        this.setupSceneTransitions();
    }
    
    setupSceneTransitions() {
        //console.log('🔧 setupSceneTransitions() called');

        // Create a separate group for scene transition areas
        this.sceneTransitionBodies = this.physics.add.staticGroup();
        //console.log('  Created sceneTransitionBodies group:', !!this.sceneTransitionBodies);
        
        // Define scene transition rectangles
        // These use overlap detection (not collision) to trigger scene changes
        // To add more transitions: Add objects with x, y, width, height, targetScene
        // Use debug mode (Z key) to see green transition rectangles
        // Remember to add corresponding spawn points in the target scene!
        const transitionAreas = [
            // River scene transition (right edge)
            { x: 511, y: 112, width: 2, height: 64, targetScene: 'River' },
            // Beach scene transition (left edge)
            { x: 0, y: 192, width: 2, height: 64, targetScene: 'Beach' },
            // Add more transitions here as needed:
            { x: 275, y: 140, width: 32, height: 2, targetScene: 'Main' },
        ];
        
        // Create transition rectangles
        transitionAreas.forEach((area, index) => {
            const rectangle = this.add.rectangle(area.x, area.y, area.width, area.height, 0x00FF00, 0);
            this.physics.add.existing(rectangle, true); // true makes it static

            // Store the target scene in the rectangle for easy access
            rectangle.targetScene = area.targetScene;

            this.sceneTransitionBodies.add(rectangle);

            //console.log(`  Transition ${index} (${area.targetScene}): body enabled=${rectangle.body?.enable}, position=(${rectangle.x},${rectangle.y}), size=${rectangle.width}x${rectangle.height}`);
        });

        //console.log(`  Created ${transitionAreas.length} transition rectangles`);
        //console.log('  sceneTransitionBodies children count:', this.sceneTransitionBodies.children.size);

        // Set up overlap detection (not collision) for scene transitions
        this.physics.add.overlap(this.player, this.sceneTransitionBodies, (player, transitionArea) => {
            // console.log('🚪 OVERLAP DETECTED! Player touched transition area for:', transitionArea.targetScene);
            // console.log('  Player position:', player.x, player.y);
            // console.log('  Transition area:', transitionArea.x, transitionArea.y);
            this.handleSceneTransition(transitionArea.targetScene);
        });
        // console.log('  Set up overlap detector between player and scene transitions');
        // console.log('  Player object at setup:', this.player);
        // console.log('  Player body enabled:', this.player.body?.enable);
    }
    
    handleSceneTransition(targetScene) {
        // Prevent multiple rapid transitions
        if (this.isTransitioning) {
            //console.log('🚫 TRANSITION BLOCKED: isTransitioning flag is still true');
            return;
        }

        this.isTransitioning = true;
        
        // console.log(`🚪 SCENE TRANSITION: Starting transition to ${targetScene} scene`, {
        //     produceItems: this.produceItems?.length || 0,
        //     collisionBodies: !!this.collisionBodies,
        //     sceneTransitionBodies: !!this.sceneTransitionBodies,
        //     playerPhysics: !!this.player?.body
        // });
        
        // Define spawn point data for the target scene based on current scene
        let sceneData = {};
        if (targetScene === 'River') {
            sceneData = { spawnPoint: 'fromFarm' };
        } else if (targetScene === 'Beach') {
            sceneData = { spawnPoint: 'fromFarm' };
        } else if (targetScene === 'MainScene') {
            sceneData = { spawnPoint: 'fromFarm' };
        }
        // Add more spawn point mappings here as needed for other scenes
        
        // Optional: Add transition effect (fade out, etc.)
        this.cameras.main.fadeOut(300, 0, 0, 0);
        
        this.cameras.main.once('camerafadeoutcomplete', () => {
            //console.log(`🚪 SCENE TRANSITION: Fade complete, transitioning to ${targetScene}`);

            // Don't manually call cleanup() - scene.start() triggers 'shutdown' event which handles cleanup
            // This prevents double cleanup which was breaking collision bodies on scene return

            // Change to the target scene with spawn data
            this.scene.start(targetScene, sceneData);
        });
    }

    setupInputHandlers() {
        // Add any specific input handlers for farm interactions
      /*   this.input.keyboard.on('keydown-SPACE', () => {
            // Handle farmplot interactions
            this.handleFarmplotInteraction();
        }); */
        
        // Setup click handlers for tool usage
        this.input.on('pointerdown', (pointer) => {
            this.handleToolClick(pointer);
        });
    }
    
    isPointerOverUI(pointer) {
        // Check if pointer is over any UI elements
        // UI elements use setScrollFactor(0) so they're positioned in screen coordinates
        const screenX = pointer.x;
        const screenY = pointer.y;
        
        // Check ToolUI areas
        if (this.toolUI && this.toolUI.slot1Area && this.toolUI.slot2Area) {
            const slot1Bounds = this.toolUI.slot1Area.getBounds();
            const slot2Bounds = this.toolUI.slot2Area.getBounds();
            
            if (slot1Bounds.contains(screenX, screenY) || slot2Bounds.contains(screenX, screenY)) {
                //console.log('UI interaction detected: ToolUI slot');
                return true;
            }
        }
        
        // Check ToolSelectionUI (if visible)
        if (this.toolSelectionUI && this.toolSelectionUI.isVisible) {
            // ToolSelectionUI creates interactive areas for each tool slot
            // Check if pointer is within the UI bounds (approximate check)
            const toolSelectionBounds = {
                x: this.toolSelectionUI.x - 128, // UI is 256 wide centered
                y: this.toolSelectionUI.y - 96,  // UI is 192 tall centered
                width: 256,
                height: 192
            };
            
            if (screenX >= toolSelectionBounds.x && 
                screenX <= toolSelectionBounds.x + toolSelectionBounds.width &&
                screenY >= toolSelectionBounds.y && 
                screenY <= toolSelectionBounds.y + toolSelectionBounds.height) {
                //console.log('UI interaction detected: ToolSelectionUI');
                return true;
            }
        }
        
        // Check SeedbagUI (if visible)
        if (this.seedbagUI && this.seedbagUI.isVisible) {
            // SeedbagUI creates interactive icons
            // Check if pointer is within the UI bounds (approximate check)
            const seedbagBounds = {
                x: this.seedbagUI.x - 128, // UI is 256 wide centered
                y: this.seedbagUI.y - 96,  // UI is 192 tall centered
                width: 256,
                height: 192
            };
            
            if (screenX >= seedbagBounds.x && 
                screenX <= seedbagBounds.x + seedbagBounds.width &&
                screenY >= seedbagBounds.y && 
                screenY <= seedbagBounds.y + seedbagBounds.height) {
                ////console.log('UI interaction detected: SeedbagUI');
                return true;
            }
        }
        
        return false;
    }
    
    handleToolClick(pointer) {
        // console.log('🔍 FarmScene: handleToolClick called', {
        //     uiBlocked: this.uiInteractionBlocked,
        //     toolSelectionOpen: this.toolSelectionUI?.isVisible,
        //     seedbagOpen: this.seedbagUI?.isVisible
        // });
        
        // Prevent tool use during tool animation
        if (this.currentToolSprite) return;
        
        // Prevent tool use if UI interaction blocking flag is set
        if (this.uiInteractionBlocked) {
            //console.log('Click blocked: UI interaction in progress');
            // Reset the flag after blocking
            this.uiInteractionBlocked = false;
            return;
        }
        
        // Prevent tool use if any equipment UI panels are open
        if ((this.toolSelectionUI && this.toolSelectionUI.isVisible) || 
            (this.seedbagUI && this.seedbagUI.isVisible)) {
            // console.log('Click blocked: Equipment UI panel is open', {
            //     toolSelectionOpen: this.toolSelectionUI?.isVisible,
            //     seedbagOpen: this.seedbagUI?.isVisible
            // });
            return;
        }
        
        // Prevent tool use if clicking on UI elements
        if (this.isPointerOverUI(pointer)) {
            //console.log('Click blocked: Clicking on UI element');
            return;
        }
        
        // Check for harvestable crops first (overrides tool usage)
        let harvestAttempted = false;
        if (pointer.leftButtonDown() || pointer.rightButtonDown()) {
            if (pointer.rightButtonDown()) {
                pointer.event.preventDefault(); // Prevent browser context menu
            }
            
            // Detect clicked tile based on mouse position, not player position
            const clickedTile = this.getClickedFarmableTile(pointer);
            if (clickedTile) {
                const { plotIndex, farmableRow, tileX } = clickedTile;
                const tileIndex = tileX + 1; // Convert 0-9 to 1-10
                const tileState = this.getTileState(plotIndex, farmableRow + 1, tileIndex);
                
                // Check if crop is ready for harvest (phase 7)
                if (tileState && tileState.phase === 7 && tileState.cropId) {
                    //console.log(`Harvesting crop at Plot ${plotIndex}, Row ${farmableRow + 1}, Tile ${tileIndex} - blocking tool actions`);
                    this.harvestCrop(plotIndex, farmableRow + 1, tileIndex, tileState.cropId);
                    harvestAttempted = true;
                }
            }
        }
        
        // Exit early if harvest was attempted to prevent any tool actions
        if (harvestAttempted) {
            return;
        }
        
        // Check if player has a tool equipped
        const equippedTools = this.toolUI ? this.toolUI.getEquippedTools() : { slot1: null, slot2: null };
        let selectedTool = null;
        
        if (pointer.leftButtonDown() && equippedTools.slot1) {
            selectedTool = equippedTools.slot1;
        } else if (pointer.rightButtonDown() && equippedTools.slot2) {
            selectedTool = equippedTools.slot2;
        }
        
        if (!selectedTool) {
            //console.log('No tool equipped for this click type');
            return;
        }
        
        // Handle tool usage based on type
        if (selectedTool.startsWith('Tool_')) {
            this.handleToolUse(selectedTool, pointer);
        } else if (selectedTool.startsWith('Seeds_')) {
            this.handleSeedUse(selectedTool, pointer);
        }
    }
    
    handleToolUse(toolId, pointer) {
        // Check if player has enough energy (2 energy required)
        if (!this.statsManager) {
            console.error('StatsManager not available for energy check');
            return;
        }
        
        const currentStats = this.statsManager.getStats();
        if (currentStats.energy < 2) {
            //console.log('Not enough energy to use tool (need 2 energy)');
            return;
        }
        
        // Get current farmable tile
        const currentTile = this.getCurrentFarmableTile();
        if (!currentTile) {
            //console.log('Not on a farmable tile');
            return;
        }
        
        // Deduct energy before tool use
        this.statsManager.updateStats({ energy: currentStats.energy - 2 });
        //console.log(`Used tool: -2 energy (${currentStats.energy} → ${currentStats.energy - 2})`);
        
        // Get tool type from ID
        const toolMap = {
            'Tool_0': 'hoe',
            'Tool_1': 'wateringcan',
            'Tool_2': 'axe',
            'Tool_3': 'pickaxe',
            'Tool_4': 'scythe'
        };
        
        const toolType = toolMap[toolId];
        if (!toolType) {
            //console.log('Unknown tool type:', toolId);
            return;
        }
        
        // Handle specific tool actions
        switch (toolType) {
            case 'hoe':
                this.handleHoeAction(currentTile);
                break;
            case 'wateringcan':
                this.handleWateringCanAction(currentTile);
                break;
            case 'scythe':
                this.handleScytheAction(currentTile);
                break;
            default:
                console.log(`Tool ${toolType} not yet implemented for farming`);
        }
        
        // Show tool animation
        this.showToolAnimation(toolType);
    }
    
    handleSeedUse(seedId, pointer) {
        // Check if player has this seed in inventory
        const inventory = inventoryManager.getInventory();
        const seedItem = inventory.find(item => item.itemCode === seedId);
        if (!seedItem || seedItem.quantity <= 0) {
            //console.log(`No ${seedId} in inventory to plant`);
            return;
        }
        
        // Get current farmable tile
        const currentTile = this.getCurrentFarmableTile();
        if (!currentTile) {
            //console.log('Not on a farmable tile');
            return;
        }
        
        const { plotIndex, farmableRow, tileX } = currentTile;
        const tileIndex = tileX + 1; // Convert 0-9 to 1-10
        
        // Get current tile state
        const tileState = this.getTileState(plotIndex, farmableRow + 1, tileIndex); // farmableRow is 0-2, need 1-3
        
        //console.log(`Seed planting action on plot ${plotIndex}, row ${farmableRow + 1}, tile ${tileIndex}`, tileState);
        
        // Only plant on tilled tiles (phase 1) that don't already have crops
        if (tileState.phase === 1 && (!tileState.cropId || tileState.cropId === 0)) {
            // Look up the seed item to get the actual crop ID
            const seedItem = itemManager.getItemData(seedId);
            if (!seedItem || !seedItem.cropId) {
                console.error(`Invalid seed item: ${seedId}`);
                return;
            }
            this.plantSeed(plotIndex, farmableRow + 1, tileIndex, seedItem.cropId);
        } else if (tileState.phase === 0) {
            //console.log('Tile needs to be tilled before planting seeds');
        } else if (tileState.phase === 10) {
            //console.log('Dead crops must be cleared with a scythe before planting');
        } else if (tileState.phase >= 2) {
            //console.log('Tile already has living crops planted');
        } else {
            //console.log('Cannot plant seeds on this tile');
        }
    }
    
    async plantSeed(plotIndex, farmableRow, tileIndex, cropId) {
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        
        // Update local state to planted (phase 2) IMMEDIATELY for responsive UI
        this.tileStates.set(key, { 
            phase: 2, // Phase 2 = planted
            cropId: cropId,
            timesWatered: 0,
            plantedAt: Date.now(),
            lastPhaseTime: Date.now(),
            deathTimer: Date.now() + this.SEED_DEATH_TIMER // Dies in 5 minutes if not watered
        });
        
        // Remove raw tile sprite for this position (if it exists) IMMEDIATELY
        this.removeRawTileSprite(plotIndex, farmableRow, tileIndex);
        
        // Add planted seed sprite for this position IMMEDIATELY
        this.addPlantedSeedSprite(plotIndex, farmableRow, tileIndex, cropId);
        
        //(`Planted seed: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}, Crop ${cropId}`);
        
        // Set up 5-minute timer for crop death if not watered
        this.setupCropDeathTimer(plotIndex, farmableRow, tileIndex);
        
        // Handle backend sync and inventory consumption in background (non-blocking)
        try {
            // Consume one seed from player's inventory first
            await this.consumeSeed(cropId);
            
            // Call backend to plant the seed
            const gameService = window.gameService;
            if (gameService) {
                await gameService.plantCrop(plotIndex, farmableRow, tileIndex, cropId);
                //console.log(`Successfully synced planted crop ${cropId} to database: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}`);
            }
        } catch (error) {
            console.error('Error syncing planted seed to database:', error);
            // Revert local state on error
            const currentState = this.tileStates.get(key);
            this.tileStates.set(key, { ...currentState, phase: 1, cropId: 0 }); // Revert to tilled
            
            // Revert visual state
            this.removePlantedSeedSprite(plotIndex, farmableRow, tileIndex);
            // Note: Don't add raw tile sprite back since we reverted to tilled (phase 1)
        }
    }
    
    addPlantedSeedSprite(plotIndex, farmableRow, tileIndex, cropId) {
        // Check if planted seed sprite already exists at this position
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        // Use adjusted Y position to match where planted seed sprites are positioned
        const worldY = plotTop + (tileY * 16) + 8 - 8;
        
        // Check if planted seed sprite already exists at this position
        const existingSprite = this.plantedSeedSprites.find(sprite => {
            return Math.abs(sprite.x - worldX) < 1 && Math.abs(sprite.y - worldY) < 1;
        });
        
        if (!existingSprite) {
            const sprite = this.createPlantedSeedSprite(plotIndex, farmableRow, tileIndex, cropId, 2);
            if (sprite) {
                this.plantedSeedSprites.push(sprite);
                //console.log('Added planted seed sprite');
            }
        }
    }
    
    async harvestCrop(plotIndex, farmableRow, tileIndex, cropId) {
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        
        // Check if player has enough energy (1 energy required for harvesting)
        if (!this.statsManager) {
            console.error('StatsManager not available for energy check');
            return;
        }
        
        const currentStats = this.statsManager.getStats();
        if (currentStats.energy < 1) {
            //console.log('Not enough energy to harvest crop (need 1 energy)');
            return;
        }
        
        // Immediately remove crop from database to prevent double harvesting
        const gameService = window.gameService;
        if (gameService) {
            // Fire and forget - remove crop entry immediately for duplication prevention
            gameService.removeWitheredCrop(plotIndex, farmableRow, tileIndex)
                .then(() => {
                    //console.log(`✅ Immediately removed harvested crop from database: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}`);
                })
                .catch((error) => {
                    console.error('❌ Error removing harvested crop from database:', error);
                    // Don't revert local changes - just log the error
                });
        } else {
            console.warn('GameService not available for harvest sync - proceeding with local changes only');
        }
        
        // Proceed immediately with local changes for responsive gameplay
        
        // Deduct energy immediately
        this.statsManager.updateStats({ energy: currentStats.energy - 1 });
        //console.log(`Harvested crop: -1 energy (${currentStats.energy} → ${currentStats.energy - 1})`);
        
        // Check if this is a mutant crop
        const isMutant = this.isMutantCrop(plotIndex, farmableRow, tileIndex);
        
        // Play appropriate harvest sound
        if (isMutant) {
            this.sound.play('mutant-wow', { volume: 0.7 });
        } else {
            const harvestSounds = ['harvest-crop-1', 'harvest-crop-2'];
            const randomHarvestSound = harvestSounds[Math.floor(Math.random() * harvestSounds.length)];
            this.sound.play(randomHarvestSound, { volume: 0.5 });
        }
        
        // Remove the mature crop sprite immediately
        this.removePlantedSeedSprite(plotIndex, farmableRow, tileIndex);
        
        // Remove sparkle animation for this crop (both regular and mutant)
        this.removeSparkleAnimation(plotIndex, farmableRow, tileIndex);
        
        // Remove watered tile sprite (reset watered state after harvest)
        this.removeWateredTileSprite(plotIndex, farmableRow, tileIndex);
        
        // Completely reset tile state to clean raw state (phase 0) - clear all properties
        this.tileStates.set(key, { 
            phase: 0 
            // No other properties - completely clean state
        });
        
        // Clear any existing growth timers for this tile
        const growthTimerKey = `${plotIndex}_${farmableRow}_${tileIndex}`;
        if (this.growthTimers.has(growthTimerKey)) {
            const timer = this.growthTimers.get(growthTimerKey);
            if (timer) {
                clearTimeout(timer); // Use clearTimeout for setTimeout timers
            }
            this.growthTimers.delete(growthTimerKey);
            //console.log(`Cleared growth timer for harvested tile: ${growthTimerKey}`);
        }
        
        // Clean up debug mutant tracking
        this.debugMutantCrops.delete(key);
        
        // Add raw tile sprite back
        const rawSprite = this.createRawTileSprite(plotIndex, farmableRow, tileIndex);
        if (rawSprite) {
            this.rawTileSprites.push(rawSprite);
        }
        
        // Spawn harvest rewards (crop + seedbags + mutant bonuses)
        this.spawnHarvestRewards(plotIndex, farmableRow, tileIndex, cropId, isMutant);
        
        //console.log(`Harvested ${isMutant ? 'MUTANT ' : ''}crop: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}, Crop ${cropId}`);
        //console.log(`Tile completely reset to clean raw state: ${key}`);
    }
    
    spawnProduceItem(plotIndex, farmableRow, tileIndex, cropId) {
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        // Get crop texture from ItemManager
        const cropItem = itemManager.getItemData(`Consumable_${cropId}`);
        if (!cropItem) {
            console.warn(`Crop item ${cropId} not found for produce spawn`);
            return;
        }
        
        // Calculate spawn position
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        const worldY = plotTop + (tileY * 16) + 8 - 8;
        
        // Create produce sprite using frame 0 (0-indexed) of the crop spritesheet
        const produceSprite = this.add.image(worldX, worldY, cropItem.textureKey, 0);
        produceSprite.setDepth(2.8); // Above crop sprites
        produceSprite.setOrigin(0.5, 0.5);
        
        // Store produce data
        const produceData = {
            sprite: produceSprite,
            cropId: cropId,
            picked: false,
            position: { x: worldX, y: worldY },
            baseY: worldY,
            textureKey: cropItem.textureKey
        };
        
        this.produceItems.push(produceData);
        
        // Create falling animation with slight arc
        const fallDistance = 8; // Fall 8 pixels
        const arcDistance = 4; // 4 pixel horizontal arc
        
        this.tweens.add({
            targets: produceSprite,
            x: worldX + arcDistance,
            y: worldY + fallDistance,
            duration: 300,
            ease: 'Quad.easeOut',
            onComplete: () => {
                // Start floating animation
                this.startFloatingAnimation(produceData);
            }
        });
    }
    
    startFloatingAnimation(produceData) {
        // Create subtle floating animation (5 pixels up and down)
        this.tweens.add({
            targets: produceData.sprite,
            y: produceData.baseY + 5, // Float up 5 pixels
            duration: 1500,
            ease: 'Sine.easeInOut',
            yoyo: true,
            repeat: -1 // Infinite
        });
    }
    
    async checkExpiredCropsAndTiles() {
        const currentTime = Date.now();
        const tilesToUpdate = [];
        
        this.tileStates.forEach((tileData, key) => {
            const [plot, row, tile] = key.split('_').map(Number);
            
            // Check tilled tiles that should revert to raw (15 minutes without planting)
            if (tileData.phase === 1 && tileData.revertTime && currentTime > tileData.revertTime) {
                //console.log(`Reverting tilled tile back to raw: Plot ${plot}, Row ${row}, Tile ${tile}`);
                tilesToUpdate.push({
                    key,
                    newState: { phase: 0 },
                    action: 'revert_to_raw',
                    plot, row, tile
                });
            }
            
            // Check planted crops that should die (5 minutes without watering)
            else if (tileData.phase === 2 && tileData.deathTimer && currentTime > tileData.deathTimer) {
                //console.log(`Crop died due to lack of water: Plot ${plot}, Row ${row}, Tile ${tile}`);
                tilesToUpdate.push({
                    key,
                    newState: { 
                        ...tileData,
                        phase: 10, // Phase 10 = dead crop
                        diedAt: currentTime
                    },
                    action: 'kill_crop',
                    plot, row, tile
                });
            }
        });
        
        // Sync expired tile changes to database
        if (tilesToUpdate.length > 0) {
            await this.syncExpiredTilesToDatabase(tilesToUpdate);
        }
        
        // Apply all visual updates after database sync
        tilesToUpdate.forEach(update => {
            this.tileStates.set(update.key, update.newState);
            
            if (update.action === 'revert_to_raw') {
                // Add raw tile sprite
                const sprite = this.createRawTileSprite(update.plot, update.row, update.tile);
                if (sprite) {
                    this.rawTileSprites.push(sprite);
                }
            } else if (update.action === 'kill_crop') {
                // Remove planted seed sprite
                this.removePlantedSeedSprite(update.plot, update.row, update.tile);
                // Add dead crop sprite (frame 11 of the crop)
                const tileState = this.tileStates.get(update.key);
                if (tileState && tileState.cropId) {
                    const deadSprite = this.createDeadCropSprite(update.plot, update.row, update.tile, tileState.cropId);
                    if (deadSprite) {
                        this.deadCropSprites.push(deadSprite);
                    }
                }
            }
        });
        
        if (tilesToUpdate.length > 0) {
            //console.log(`Updated ${tilesToUpdate.length} expired tiles/crops and synced to database`);
        }
    }
    
    async syncExpiredTilesToDatabase(tilesToUpdate) {
        const gameService = window.gameService;
        if (!gameService) {
            console.warn('GameService not available, skipping database sync for expired tiles');
            return;
        }
        
        const syncPromises = tilesToUpdate.map(async (update) => {
            try {
                if (update.action === 'revert_to_raw') {
                    // Delete the database entry for reverted tiles
                    // Use removeWitheredCrop as a temporary solution to clear the entry
                    await gameService.removeWitheredCrop(update.plot, update.row, update.tile);
                    //console.log(`Database: Cleared expired tilled tile: Plot ${update.plot}, Row ${update.row}, Tile ${update.tile}`);
                } else if (update.action === 'kill_crop') {
                    // Delete the database entry for dead crops
                    // Use removeWitheredCrop to clear the entry (dead crops = withered crops)
                    await gameService.removeWitheredCrop(update.plot, update.row, update.tile);
                    //(`Database: Killed expired crop: Plot ${update.plot}, Row ${update.row}, Tile ${update.tile}`);
                }
            } catch (error) {
                console.error(`Failed to sync expired tile to database: Plot ${update.plot}, Row ${update.row}, Tile ${update.tile}`, error);
                // Don't throw - continue with other tiles
            }
        });
        
        // Wait for all database operations to complete
        await Promise.allSettled(syncPromises);
    }
    
    removeWateredTileSprite(plotIndex, farmableRow, tileIndex) {
        // Find and remove the watered tile sprite at this position
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        const worldY = plotTop + (tileY * 16) + 8;
        
        // Find sprite at this position
        const spriteIndex = this.wateredTileSprites.findIndex(sprite => {
            return Math.abs(sprite.x - worldX) < 1 && Math.abs(sprite.y - worldY) < 1;
        });
        
        if (spriteIndex !== -1) {
            this.wateredTileSprites[spriteIndex].destroy();
            this.wateredTileSprites.splice(spriteIndex, 1);
            //console.log('Removed watered tile sprite');
        }
    }
    
    setupCropDeathTimer(plotIndex, farmableRow, tileIndex) {
        // Set up timer for crop death if not watered within 5 minutes
        this.time.delayedCall(this.SEED_DEATH_TIMER, () => { // 5 minutes
            const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
            const tileState = this.tileStates.get(key);
            
            // Only kill crop if still planted (phase 2) and not watered
            if (tileState && tileState.phase === 2 && (!tileState.timesWatered || tileState.timesWatered === 0)) {
                //console.log(`Crop died due to lack of water: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}`);
                
                // Update state to dead crop (phase 10)
                this.tileStates.set(key, { 
                    ...tileState,
                    phase: 10, // Phase 10 = dead crop
                    diedAt: Date.now()
                });
                
                // Remove planted seed sprite
                this.removePlantedSeedSprite(plotIndex, farmableRow, tileIndex);
                
                // Add dead crop sprite (frame 11 of the crop)
                if (tileState.cropId) {
                    const deadSprite = this.createDeadCropSprite(plotIndex, farmableRow, tileIndex, tileState.cropId);
                    if (deadSprite) {
                        this.deadCropSprites.push(deadSprite);
                    }
                }
            }
        });
    }
    
    removePlantedSeedSprite(plotIndex, farmableRow, tileIndex) {
        // Find and remove the planted seed sprite at this position
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        // Use adjusted Y position to match where planted seed sprites are positioned
        const worldY = plotTop + (tileY * 16) + 8 - 8;
        
        // Find sprite at this position
        const spriteIndex = this.plantedSeedSprites.findIndex(sprite => {
            return Math.abs(sprite.x - worldX) < 1 && Math.abs(sprite.y - worldY) < 1;
        });
        
        if (spriteIndex !== -1) {
            this.plantedSeedSprites[spriteIndex].destroy();
            this.plantedSeedSprites.splice(spriteIndex, 1);
            //console.log('Removed planted seed sprite');
        }
    }
    
    removeDeadCropSprite(plotIndex, farmableRow, tileIndex) {
        // Find and remove the dead crop sprite at this position
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        // Use adjusted Y position to match where dead crop sprites are positioned
        const worldY = plotTop + (tileY * 16) + 8 - 8;
        
        // Find sprite at this position
        const spriteIndex = this.deadCropSprites.findIndex(sprite => {
            return Math.abs(sprite.x - worldX) < 1 && Math.abs(sprite.y - worldY) < 1;
        });
        
        if (spriteIndex !== -1) {
            this.deadCropSprites[spriteIndex].destroy();
            this.deadCropSprites.splice(spriteIndex, 1);
            //console.log('Removed dead crop sprite');
        }
    }
    
    handleScytheAction(currentTile) {
        const { plotIndex, farmableRow, tileX } = currentTile;
        const tileIndex = tileX + 1; // Convert 0-9 to 1-10
        
        // Get current tile state
        const tileState = this.getTileState(plotIndex, farmableRow + 1, tileIndex); // farmableRow is 0-2, need 1-3
        
        //console.log(`Scythe action on plot ${plotIndex}, row ${farmableRow + 1}, tile ${tileIndex}`, tileState);
        
        // Only clear dead crops (phase 10)
        if (tileState.phase === 10) {
            this.clearDeadCrop(plotIndex, farmableRow + 1, tileIndex);
        } else {
            console.log('Scythe can only clear dead crops');
        }
    }
    
    async clearDeadCrop(plotIndex, farmableRow, tileIndex) {
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        
        // Update local state back to raw (phase 0) immediately for responsive UI
        this.tileStates.set(key, { phase: 0 });
        
        // Remove any existing planted seed sprites at this position
        this.removePlantedSeedSprite(plotIndex, farmableRow, tileIndex);
        
        // Remove any existing dead crop sprites at this position
        this.removeDeadCropSprite(plotIndex, farmableRow, tileIndex);
        
        // Remove any existing watered tile sprites at this position
        this.removeWateredTileSprite(plotIndex, farmableRow, tileIndex);
        
        // Add raw tile sprite for this position
        const sprite = this.createRawTileSprite(plotIndex, farmableRow, tileIndex);
        if (sprite) {
            this.rawTileSprites.push(sprite);
        }
        
        //console.log(`Cleared dead crop: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}`);
        
        // Sync to backend in background (non-blocking)
        try {
            const gameService = window.gameService;
            if (gameService) {
                // Clear the crop from the database by calling removeWitheredCrop endpoint
                await gameService.removeWitheredCrop(plotIndex, farmableRow, tileIndex);
                //console.log(`Successfully synced cleared dead crop to database: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}`);
            }
        } catch (error) {
            console.error('Error syncing cleared dead crop to database:', error);
            // Revert local state on error
            this.tileStates.set(key, { phase: 10 }); // Revert to dead crop state
            
            // Revert visual state
            this.removeRawTileSprite(plotIndex, farmableRow, tileIndex);
            // Add dead crop sprite back
            const tileState = this.tileStates.get(key);
            if (tileState && tileState.cropId) {
                const deadSprite = this.createDeadCropSprite(plotIndex, farmableRow, tileIndex, tileState.cropId);
                if (deadSprite) {
                    this.deadCropSprites.push(deadSprite);
                }
            }
        }
    }
    
    handleHoeAction(currentTile) {
        const { plotIndex, farmableRow, tileX } = currentTile;
        const tileIndex = tileX + 1; // Convert 0-9 to 1-10
        
        // Get current tile state
        const tileState = this.getTileState(plotIndex, farmableRow + 1, tileIndex); // farmableRow is 0-2, need 1-3
        
        //console.log(`Hoe action on plot ${plotIndex}, row ${farmableRow + 1}, tile ${tileIndex}`, tileState);
        
        // Only till raw (phase 0) tiles
        if (tileState.phase === 0) {
            this.tillTile(plotIndex, farmableRow + 1, tileIndex);
        } else if (tileState.phase === 10) {
            console.log('Dead crops must be cleared with a scythe before tilling');
        } else {
            console.log('Tile is already tilled or occupied');
        }
    }
    
    handleWateringCanAction(currentTile) {
        const { plotIndex, farmableRow, tileX } = currentTile;
        const tileIndex = tileX + 1; // Convert 0-9 to 1-10
        
        // Get current tile state
        const tileState = this.getTileState(plotIndex, farmableRow + 1, tileIndex); // farmableRow is 0-2, need 1-3
        
        // Only water tiles that have been sown with seeds (phase 2+) and crops
        if (tileState.phase >= 2 && tileState.cropId && tileState.cropId > 0) {
            this.waterTile(plotIndex, farmableRow + 1, tileIndex);
        } else {
            console.log('Tile needs to be sown with seeds before watering');
        }
    }
    
    async waterTile(plotIndex, farmableRow, tileIndex) {
        try {
            const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
            const currentState = this.tileStates.get(key);
            
            if (!currentState || currentState.phase < 2) {
                return;
            }
            
            // Determine if this is growth-triggering water (seeds only) or quality bonus water
            const isGrowthTrigger = currentState.phase === 2 && (currentState.timesWatered || 0) === 0;
            const isQualityBonus = currentState.phase >= 2 && currentState.phase < 7; // Exclude phase 7
            const isHarvestableWater = currentState.phase === 7; // Phase 7 watering allowed but not tracked
            
            // Call backend to increment water count (for quality tracking) - exclude phase 7
            const gameService = window.gameService;
            if (gameService && isQualityBonus) {
                await gameService.waterCrop(plotIndex, farmableRow, tileIndex);
            } 
            
            // Update local state with watering info (only track quality waterings for non-harvestable crops)
            const newWaterCount = isHarvestableWater ? currentState.timesWatered : (currentState.timesWatered || 0) + 1;
            this.tileStates.set(key, { 
                ...currentState,
                timesWatered: newWaterCount,
                lastWatered: Date.now(),
                qualityWaterings: (currentState.qualityWaterings || 0) + (isQualityBonus ? 1 : 0),
                deathTimer: null // Reset death timer since crop was watered
            });
            
            // Add watered tile sprite for this position
            this.addWateredTileSprite(plotIndex, farmableRow, tileIndex);
            
            // If this is the first watering of seeds, start growth progression
            if (isGrowthTrigger) {
                this.startGrowthProgression(plotIndex, farmableRow, tileIndex);
            }
            
            //console.log(`Watered tile: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex} (Growth trigger: ${isGrowthTrigger})`);
            
        } catch (error) {
            console.error('Error watering tile:', error);
        }
    }
    
    addWateredTileSprite(plotIndex, farmableRow, tileIndex) {
        // Check if watered sprite already exists at this position
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        const worldY = plotTop + (tileY * 16) + 8;
        
        // Check if watered sprite already exists at this position
        const existingSprite = this.wateredTileSprites.find(sprite => {
            return Math.abs(sprite.x - worldX) < 1 && Math.abs(sprite.y - worldY) < 1;
        });
        
        if (!existingSprite) {
            const sprite = this.createWateredTileSprite(plotIndex, farmableRow, tileIndex);
            if (sprite) {
                this.wateredTileSprites.push(sprite);
                //console.log('Added watered tile sprite');
            }
        }
    }
    
    async tillTile(plotIndex, farmableRow, tileIndex) {
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        
        // Update local state to tilled (phase 1) IMMEDIATELY for responsive UI
        this.tileStates.set(key, { 
            phase: 1, 
            tilledAt: Date.now(),
            revertTime: Date.now() + (15 * 60 * 1000) // 15 minutes from now
        });
        
        // Remove raw tile sprite for this position IMMEDIATELY
        this.removeRawTileSprite(plotIndex, farmableRow, tileIndex);
                
        // Set up 15-minute timer to revert to raw if not planted
        this.setupTileRevertTimer(plotIndex, farmableRow, tileIndex);
        
        // Call backend to sync in background (non-blocking)
        try {
            const gameService = window.gameService;
            if (gameService) {
                await gameService.tillTile(plotIndex, farmableRow, tileIndex);
            }
        } catch (error) {
            console.error('Error syncing tilled tile to database:', error);
            // Revert local state on error
            this.tileStates.set(key, { phase: 0 });
            // Add raw tile sprite back
            const sprite = this.createRawTileSprite(plotIndex, farmableRow, tileIndex);
            if (sprite) {
                this.rawTileSprites.push(sprite);
            }
        }
    }
    
    removeRawTileSprite(plotIndex, farmableRow, tileIndex) {
        // Find and remove the raw tile sprite at this position
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        const worldY = plotTop + (tileY * 16) + 8;
        
        // Find sprite at this position
        const spriteIndex = this.rawTileSprites.findIndex(sprite => {
            return Math.abs(sprite.x - worldX) < 1 && Math.abs(sprite.y - worldY) < 1;
        });
        
        if (spriteIndex !== -1) {
            this.rawTileSprites[spriteIndex].destroy();
            this.rawTileSprites.splice(spriteIndex, 1);
        }
    }
    
    setupTileRevertTimer(plotIndex, farmableRow, tileIndex) {
        // Set up timer to revert tile to raw after 15 minutes if not planted
        this.time.delayedCall(15 * 60 * 1000, () => { // 15 minutes
            const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
            const tileState = this.tileStates.get(key);
            
            // Only revert if still tilled (phase 1) and not planted
            if (tileState && tileState.phase === 1) {
                
                // Update state back to raw
                this.tileStates.set(key, { phase: 0 });
                
                // Recreate raw tile sprite
                const sprite = this.createRawTileSprite(plotIndex, farmableRow, tileIndex);
                if (sprite) {
                    this.rawTileSprites.push(sprite);
                }
            }
        });
    }
    
    showToolAnimation(toolType) {
        if (this.currentToolSprite) return; // Animation already in progress
        
        // Get player direction
        const playerDirection = this.player.lastDirection || 'right';
        
        // Create tool sprite
        const textureMap = {
            'hoe': 'tool-hoe',
            'wateringcan': 'tool-watering-can',
            'axe': 'tool-axe',
            'pickaxe': 'tool-pickaxe',
            'scythe': 'tool-scythe'
        };
        
        const textureKey = textureMap[toolType];
        if (!textureKey) return;
        
        if (toolType === 'hoe') {
            this.showHoeAnimation(playerDirection, textureKey);
        } else if (toolType === 'wateringcan') {
            this.showWateringCanAnimation(playerDirection, textureKey);
        } else if (toolType === 'scythe') {
            this.showScytheAnimation(playerDirection, textureKey);
        } else {
            this.showGenericToolAnimation(playerDirection, textureKey);
        }
    }
    
    showHoeAnimation(playerDirection, textureKey) {
        // Starting position for hoe (in front of player)
        let startX = this.player.x;
        let startY = this.player.y + 14;
        let startRotation = 0;
        let flipY = false;
        let loopCenterX = 0;
        let loopCenterY = 0;
        
        switch (playerDirection) {
            case 'left':
                startX -= 5;
                flipY = true;
                startRotation = 7 * (Math.PI / 12); // 135 degrees
                loopCenterX = startX - 5; // Loop center 5px away from user
                loopCenterY = startY;
                break;
            case 'right':
                startX += 5;
                startRotation = Math.PI / 4; // 45 degrees
                loopCenterX = startX + 5; // Loop center 5px away from user
                loopCenterY = startY;
                break;
        }
        
        // Create hoe sprite at starting position
        this.currentToolSprite = this.add.image(startX, startY, textureKey);
        this.currentToolSprite.setDepth(6); // Above player
        this.currentToolSprite.setRotation(startRotation);
        this.currentToolSprite.setFlipY(flipY);
        
        // Play random hoe hit sound effect
        const hoeSound = Math.random() < 0.5 ? 'hoe-hit-1' : 'hoe-hit-2';
        this.sound.play(hoeSound, { volume: 0.6 });
        
        // Create circular hoeing motion using a timeline of small steps
        let currentAngle = 0;
        const totalSteps = 8;
        const stepDuration = 40; // 320ms total animation
        const radius = 5;
        const totalRotation = Math.PI / 4; // 45 degrees additional rotation
        
        // Animate the circular motion in steps
        const animateStep = (step) => {
            if (step >= totalSteps) {
                // Animation complete - clean up
                if (this.currentToolSprite) {
                    this.currentToolSprite.destroy();
                    this.currentToolSprite = null;
                }
                return;
            }
            
            // Calculate position on circle
            const progress = step / totalSteps;
            const angle = playerDirection === 'left' 
                ? -progress * Math.PI * 2  // Counter-clockwise for left
                : progress * Math.PI * 2;  // Clockwise for right
            const offsetX = Math.cos(angle) * radius;
            const offsetY = Math.sin(angle) * radius;
            
            // Calculate current rotation (start rotation + progress * additional rotation)
            const currentRotation = startRotation + (progress * totalRotation);
            
            this.tweens.add({
                targets: this.currentToolSprite,
                x: loopCenterX + offsetX,
                y: loopCenterY + offsetY,
                rotation: currentRotation,
                duration: stepDuration,
                ease: step < totalSteps / 2 ? 'Power2.easeOut' : 'Power2.easeIn',
                onComplete: () => {
                    animateStep(step + 1);
                }
            });
        };
        
        // Start the animation
        animateStep(0);
    }
    
    showWateringCanAnimation(playerDirection, textureKey) {
        // Starting position for watering can (in front of player)
        let startX = this.player.x;
        let startY = this.player.y + 10;
        let startRotation = 0;
        let flipX = false;
        let waterX = 0;
        let waterY = 0;
        
        switch (playerDirection) {
            case 'up':
                startY -= 16;
                startRotation = 0;
                waterX = startX + 3;
                waterY = startY - 8;
                break;
            case 'down':
                startY += 16;
                startRotation = 0;
                waterX = startX + 3;
                waterY = startY + 8;
                break;
            case 'left':
                startX -= 16;
                startRotation = 0;
                flipX = true;
                waterX = startX - 5;
                waterY = startY + 5;
                break;
            case 'right':
                startX += 16;
                startRotation = 0;
                waterX = startX + 5;
                waterY = startY + 5;
                break;
        }
        
        // Create watering can sprite at starting position
        this.currentToolSprite = this.add.image(startX, startY, textureKey);
        this.currentToolSprite.setDepth(6); // Above player
        this.currentToolSprite.setRotation(startRotation);
        this.currentToolSprite.setFlipX(flipX);
        
        // Create water animation sprite
        const waterSprite = this.add.sprite(waterX, waterY, 'tool-wateringcan-water', 0);
        waterSprite.setDepth(6); // Same depth as tool
        waterSprite.setOrigin(0.5, 0);
        waterSprite.setVisible(false);
        
        // Phase 1: Rotate watering can 90 degrees
        const rotationDirection = playerDirection === 'left' ? -1 : 1;
        const targetRotation = startRotation + (Math.PI / 2) * rotationDirection;
        
        this.tweens.add({
            targets: this.currentToolSprite,
            rotation: targetRotation,
            duration: 200,
            ease: 'Power2.easeOut',
            onComplete: () => {
                // Show and play water animation
                waterSprite.setVisible(true);
                
                // Play random watering can pour sound effect
                const wateringSound = Math.random() < 0.5 ? 'watering-can-pour-1' : 'watering-can-pour-2';
                this.sound.play(wateringSound, { volume: 0.5 });
                
                // Create water animation if it doesn't exist
                if (!this.anims.exists('water-pour')) {
                    const waterFrames = [];
                    for (let i = 0; i < 9; i++) {
                        waterFrames.push({ key: 'tool-wateringcan-water', frame: i });
                    }
                    
                    this.anims.create({
                        key: 'water-pour',
                        frames: waterFrames,
                        frameRate: 12,
                        repeat: 0
                    });
                }
                
                waterSprite.play('water-pour');
                
                // Phase 2: Rotate back to original position
                this.tweens.add({
                    targets: this.currentToolSprite,
                    rotation: startRotation,
                    duration: 200,
                    ease: 'Power2.easeIn',
                    delay: 400, // Wait for water animation
                    onComplete: () => {
                        // Clean up sprites
                        if (this.currentToolSprite) {
                            this.currentToolSprite.destroy();
                            this.currentToolSprite = null;
                        }
                        waterSprite.destroy();
                    }
                });
            }
        });
    }

    showScytheAnimation(playerDirection, textureKey) {
        // Starting position for scythe (in front of player)
        let startX = this.player.x;
        let startY = this.player.y + 10;
        let startRotation = 0;
        let flipX = false;
        let waterX = 0;
        let waterY = 0;
        
        switch (playerDirection) {
            case 'up':
                startY -= 16;
                startRotation = 0;
                waterX = startX + 3;
                waterY = startY - 8;
                break;
            case 'down':
                startY += 16;
                startRotation = 0;
                waterX = startX + 3;
                waterY = startY + 8;
                break;
            case 'left':
                startX -= 16;
                startRotation = 0;
                flipX = true;
                waterX = startX - 5;
                waterY = startY + 5;
                break;
            case 'right':
                startX += 16;
                startRotation = 0;
                waterX = startX + 5;
                waterY = startY + 5;
                break;
        }
        
        // Create scythe sprite at starting position
        this.currentToolSprite = this.add.image(startX, startY, textureKey);
        this.currentToolSprite.setDepth(6); // Above player
        this.currentToolSprite.setRotation(startRotation);
        this.currentToolSprite.setFlipX(flipX);
        
        // Set origin for more natural rotation (closer to handle/player end)
        // For a 17x17 sprite, (0.5, 0.85) puts rotation point near bottom of handle
        this.currentToolSprite.setOrigin(0.5, 0.75);
        
        // Create water animation sprite
        const scytheSprite = this.add.sprite(waterX, waterY, 'tool-scythe', 0);
        scytheSprite.setDepth(6); // Same depth as tool
        scytheSprite.setOrigin(0, 0.5);
        scytheSprite.setVisible(false);
        
        // Phase 1: Rotate scythe 60 degrees
        const rotationDirection = playerDirection === 'left' ? -1 : 1;
        const targetRotation = startRotation + (Math.PI / 3) * rotationDirection;
        
        this.tweens.add({
            targets: this.currentToolSprite,
            rotation: targetRotation,
            duration: 200,
            ease: 'Power2.easeOut',
            onComplete: () => {
                
                // Play random scythe swing sound effect
                const scytheSound = Math.random() < 0.5 ? 'scythe-swing-1' : 'scythe-swing-2';
                this.sound.play(scytheSound, { volume: 0.5 });
                
                if (this.currentToolSprite) {
                    this.currentToolSprite.destroy();
                    this.currentToolSprite = null;
                }
            }
        });
    }
    
    showGenericToolAnimation(playerDirection, textureKey) {
        // Fallback animation for other tools
        let toolX = this.player.x;
        let toolY = this.player.y;
        let rotation = 0;
        
        switch (playerDirection) {
            case 'up':
                toolY -= 20;
                rotation = -Math.PI / 2;
                break;
            case 'down':
                toolY += 20;
                rotation = Math.PI / 2;
                break;
            case 'left':
                toolX -= 20;
                rotation = Math.PI;
                break;
            case 'right':
                toolX += 20;
                rotation = 0;
                break;
        }
        
        this.currentToolSprite = this.add.image(toolX, toolY, textureKey);
        this.currentToolSprite.setDepth(6);
        this.currentToolSprite.setRotation(rotation);
        
        // Simple rotation animation
        this.tweens.add({
            targets: this.currentToolSprite,
            rotation: rotation + Math.PI / 2,
            duration: 300,
            ease: 'Power2',
            onComplete: () => {
                if (this.currentToolSprite) {
                    this.currentToolSprite.destroy();
                    this.currentToolSprite = null;
                }
            }
        });
    }

    setupDebugMode() {
        // H key to return to main scene
       
        // Toggle debug mode with 'Z' key
        this.input.keyboard.on('keydown-Z', () => {
            this.debugMode = !this.debugMode;
            this.updateDebugVisuals();
            this.updateCollisionVisuals(); // Update collision rectangle visibility
        });


        // State testing controls (for debugging the new animation system)
        this.input.keyboard.on('keydown-ONE', async () => {
            await this.player.modifyHunger(-20);
        });
        
        this.input.keyboard.on('keydown-TWO', async () => {
            await this.player.modifyHunger(20);
        });

        this.input.keyboard.on('keydown-U', async () => {
            await this.player.modifyEnergy(-20);
        });
        
        this.input.keyboard.on('keydown-I', async () => {
            await this.player.modifyEnergy(20);
        });

        this.input.keyboard.on('keydown-O', async () => {
             await this.player.modifyHealth(-20);
         });
         
         this.input.keyboard.on('keydown-P', async () => {
             await this.player.modifyHealth(20);
         });
        
        this.input.keyboard.on('keydown-THREE', () => {
            this.player.modifyHappiness(-20);
        });
        
        this.input.keyboard.on('keydown-FOUR', async () => {
            await this.player.setState({ hunger: 0, energy: 0 }); // Test critical state
        });
        
        this.input.keyboard.on('keydown-FIVE', async () => {
            await this.player.setState({ hunger: 100, energy: 100, happiness: 100 }); // Reset
        });
        
        this.input.keyboard.on('keydown-SIX', () => {
            // Toggle stat decay for testing
            if (this.player.statDecayTimer) {
                this.player.disableStatDecay();
            } else {
                this.player.enableStatDecay(1); // Fast decay for testing
            }
        });
        
        this.input.keyboard.on('keydown-SEVEN', () => {
            // Display current state information
            this.player.logStateInfo();
        });
        
        // New advanced testing controls
        this.input.keyboard.on('keydown-EIGHT', () => {
            // Toggle testing mode between local-only and database sync
            const currentMode = this.player.testingMode.syncToDatabase;
            this.player.setTestingMode({ syncToDatabase: !currentMode });
        });
        
        this.input.keyboard.on('keydown-NINE', async () => {
            // Force sync current state to database
            await this.player.forceSyncToDatabase();
        });
        
        this.input.keyboard.on('keydown-ZERO', async () => {
            // Force reload state from database
            await this.player.forceReloadFromDatabase();
        });
        
        // Display debug controls information
        // if (this.debugMode) {
        //     console.log('🎮 === USER CARE TESTING CONTROLS ===');
        //     console.log('🔧 BASIC STAT TESTING:');
        //     console.log('  1 - Decrease hunger (-20) [SYNCS TO DB]');
        //     console.log('  2 - Increase hunger (20) [SYNCS TO DB]');
        //     console.log('  U - Decrease Energy (-20) [SYNCS TO DB]');
        //     console.log('  I - Increase Energy (20) [SYNCS TO DB]');
        //     console.log('  O - Decrease Health (-20) [SYNCS TO DB]');
        //     console.log('  P - Increase Health (20) [SYNCS TO DB]');
        //     console.log('  3 - Decrease happiness (-20) [LOCAL ONLY]');
        //     console.log('  4 - Set critical state (hunger=0, energy=0) [SYNCS TO DB]');
        //     console.log('  5 - Reset all stats to 100 [SYNCS TO DB]');
        //     console.log('  6 - Toggle stat decay');
        //     console.log('  7 - Show detailed state info');
        //     console.log('');
        //     console.log('🔄 ADVANCED SYNC TESTING:');
        //     console.log('  8 - Toggle database sync mode');
        //     console.log('  9 - Force sync current state to database');
        //     console.log('  0 - Force reload state from database');
        //     console.log('');
        //     console.log('🎭 SCENE DEBUG:');
        //     console.log('  Z - Toggle debug mode (collision boxes)');
        //     console.log('  SPACE - Farmplot interaction');
        //     console.log('');
        //     console.log('💡 TIP: Use key 8 to disable database sync for rapid local testing!');
        //     console.log('=======================================');
        // }
    }
    
    updateCollisionVisuals() {
        // Update collision rectangle visibility based on debug mode
        if (this.collisionBodies && this.collisionBodies.children) {
            this.collisionBodies.children.entries.forEach(rectangle => {
                if (this.debugMode) {
                    rectangle.setStrokeStyle(2, 0xFF0000);
                    rectangle.setAlpha(0.3);
                } else {
                    rectangle.setStrokeStyle(0);
                    rectangle.setAlpha(0);
                }
            });
        }
        
        // Update scene transition rectangle visibility
        if (this.sceneTransitionBodies && this.sceneTransitionBodies.children) {
            this.sceneTransitionBodies.children.entries.forEach(rectangle => {
                if (this.debugMode) {
                    rectangle.setStrokeStyle(2, 0x00FF00);
                    rectangle.setAlpha(0.5);
                } else {
                    rectangle.setStrokeStyle(0);
                    rectangle.setAlpha(0);
                }
            });
        }
    }

    updateDebugVisuals() {
        if (this.debugMode) {
            // Add red borders to all interactive elements
            this.addDebugBorders();
            
            // Add 16x16 grid overlay
            //this.addDebugGrid();
            
            // Show unlocked farmplot sprites for debug
            this.updateFarmplotSprites();
        } else {
            // Hide collision map
            this.collisionMap.setVisible(false);
            
            // Remove debug borders
            this.removeDebugBorders();
            
            // Remove grid overlay
            this.removeDebugGrid();
            
            // Restore normal farmplot sprites
            this.updateFarmplotSprites();
        }
    }

    addDebugBorders() {
        // Add red borders to farmplots
        this.farmPlots.forEach(plot => {
            if (!plot.debugBorder) {
                plot.debugBorder = this.add.rectangle(
                    plot.position.x, 
                    plot.position.y, 
                    160, 
                    80, 
                    0xff0000, 
                    0
                ).setStrokeStyle(2, 0xff0000).setDepth(10);
            }
        });
        
        // Add blue border to dungeon fence
        if (this.dungeonFence && !this.dungeonFence.debugBorder) {
            this.dungeonFence.debugBorder = this.add.rectangle(
                4, 
                128, 
                this.dungeonFence.width, 
                this.dungeonFence.height, 
                0x0000ff, 
                0
            ).setStrokeStyle(2, 0x0000ff).setDepth(10);
        }
        
        // Add green border to vendor's (chest) collision zone
        if (this.chestCollisionZone && !this.chestCollisionZone.debugBorder) {
            this.chestCollisionZone.debugBorder = this.add.rectangle(
                432, 
                32, 
                48, 
                48, 
                0x00ff00, 
                0
            ).setStrokeStyle(2, 0x00ff00).setDepth(10);
        }
        
        // Add yellow borders to plot signs and their collision zones
        this.plotSigns.forEach(plotSign => {
            // Plot sign border (yellow)
            if (!plotSign.debugBorder) {
                plotSign.debugBorder = this.add.rectangle(
                    plotSign.position.x, 
                    plotSign.position.y, 
                    16, 
                    24, 
                    0xffff00, 
                    0
                ).setStrokeStyle(2, 0xffff00).setDepth(10);
            }
            
            // Collision zone border (orange)
            if (!plotSign.collisionDebugBorder && plotSign.collisionZone) {
                plotSign.collisionDebugBorder = this.add.rectangle(
                    plotSign.position.x, 
                    plotSign.position.y + 8, // Bottom of sign
                    16, 
                    16, 
                    0xff6600, 
                    0
                ).setStrokeStyle(2, 0xff6600).setDepth(10);
            }
            
            // Interaction zone border (cyan)
            if (!plotSign.interactionDebugBorder && plotSign.interactionZone) {
                plotSign.interactionDebugBorder = this.add.rectangle(
                    plotSign.position.x, 
                    plotSign.position.y, 
                    40, 
                    40, 
                    0x00ffff, 
                    0
                ).setStrokeStyle(2, 0x00ffff).setDepth(10);
            }
        });
        
    }

    removeDebugBorders() {
        // Remove farmplot debug borders
        this.farmPlots.forEach(plot => {
            if (plot.debugBorder) {
                plot.debugBorder.destroy();
                plot.debugBorder = null;
            }
        });
        
        // Remove dungeon fence debug border
        if (this.dungeonFence && this.dungeonFence.debugBorder) {
            this.dungeonFence.debugBorder.destroy();
            this.dungeonFence.debugBorder = null;
        }
        
        // Remove chicken coop debug border
        if (this.chickenCoop && this.chickenCoop.debugBorder) {
            this.chickenCoop.debugBorder.destroy();
            this.chickenCoop.debugBorder = null;
        }
        
        // Remove vendor's (chest) collision zone debug border
        if (this.chestCollisionZone && this.chestCollisionZone.debugBorder) {
            this.chestCollisionZone.debugBorder.destroy();
            this.chestCollisionZone.debugBorder = null;
        }
        
        // Remove plot sign debug borders
        this.plotSigns.forEach(plotSign => {
            if (plotSign.debugBorder) {
                plotSign.debugBorder.destroy();
                plotSign.debugBorder = null;
            }
            if (plotSign.collisionDebugBorder) {
                plotSign.collisionDebugBorder.destroy();
                plotSign.collisionDebugBorder = null;
            }
            if (plotSign.interactionDebugBorder) {
                plotSign.interactionDebugBorder.destroy();
                plotSign.interactionDebugBorder = null;
            }
        });
    }

    addDebugGrid() {
        if (this.debugGrid) return; // Already exists
        
        this.debugGrid = this.add.group();
        
        // Create vertical lines every 16 pixels
        for (let x = 0; x <= 512; x += 16) {
            const line = this.add.line(0, 0, x, 0, x, 384, 0x00ff00, 0.3);
            line.setOrigin(0, 0);
            line.setDepth(15);
            this.debugGrid.add(line);
        }
        
        // Create horizontal lines every 16 pixels
        for (let y = 0; y <= 384; y += 16) {
            const line = this.add.line(0, 0, 0, y, 512, y, 0x00ff00, 0.3);
            line.setOrigin(0, 0);
            line.setDepth(15);
            this.debugGrid.add(line);
        }
    }

    removeDebugGrid() {
        if (this.debugGrid) {
            this.debugGrid.destroy();
            this.debugGrid = null;
        }
    }

  /*   handleFarmplotInteraction() {
        // Check if player is on a farmable tile
        const currentTile = this.getCurrentFarmableTile();
        
        if (currentTile) {
            console.log(`Interacting with farmable tile at plot ${currentTile.plotIndex}, row ${currentTile.farmableRow}, column ${currentTile.tileX}`);
            
            // Record farming interaction for care tracking
            careManager.recordInteraction();
            
            // Add farming logic here (planting seeds, watering, etc.)
            return;
        }
    } */

    getToolDetectionPosition() {
        // Calculate detection position offset based on player's facing direction
        // This ensures the highlighted/actionable tile matches where tools appear
        let offsetX = 0;
        const offsetY = 16; // Always detect at feet level
        
        // Get player's facing direction
        const direction = this.player.lastDirection || 'right';
        
        // Offset detection position based on facing direction
        switch (direction) {
            case 'left':
                offsetX = -16; // One tile to the left
                break;
            case 'right':
                offsetX = 16; // One tile to the right
                break;
            case 'up':
            case 'down':
                // For vertical movement, use right as default
                // (could be enhanced to remember last horizontal direction)
                offsetX = 16;
                break;
        }
        
        return {
            x: this.player.x + offsetX,
            y: this.player.y + offsetY
        };
    }

    updateFarmableTileHighlight() {
        // Clear existing highlight
        if (this.farmableTileHighlight) {
            this.farmableTileHighlight.destroy();
            this.farmableTileHighlight = null;
        }
        
        // Get detection position offset in the direction the player is facing
        const detectionPos = this.getToolDetectionPosition();
        const playerX = detectionPos.x;
        const playerY = detectionPos.y;
        
        // Check each farmplot
        for (const plot of this.farmPlots) {
            // Skip locked plots unless debug mode is on
            if (!plot.unlocked && !this.debugMode) continue;
            
            // Calculate farmplot bounds (farmplots are 160x80, centered on position)
            const plotLeft = plot.position.x - 80;   // 160/2 = 80
            const plotRight = plot.position.x + 80;
            const plotTop = plot.position.y - 40;    // 80/2 = 40
            const plotBottom = plot.position.y + 40;
            
            // Expand detection bounds slightly for better user experience
            const detectionBuffer = 12;
            if (playerX >= plotLeft && playerX <= plotRight && 
                playerY >= plotTop - detectionBuffer && playerY <= plotBottom + detectionBuffer) {
                
                // Calculate which 16x16 tile the player is in relative to the farmplot
                const relativeX = playerX - plotLeft;
                const relativeY = playerY - plotTop;
                
                const tileX = Math.floor(relativeX / 16);
                const tileY = Math.floor(relativeY / 16);
                
                // Check if this tile is in a farmable row
                // Farmable rows are at y positions 0, 2, and 4 (out of 0-4)
                const isFarmableRow = (tileY === 0 || tileY === 2 || tileY === 4);
                
                // Allow detection slightly outside bounds for better UX
                const isValidTile = tileX >= 0 && tileX < 10 && tileY >= -1 && tileY <= 5;
                
                if (isFarmableRow && isValidTile) {
                    // Clamp tileY to valid range for display
                    const displayTileY = Math.max(0, Math.min(4, tileY));
                    
                    // Calculate world position of the farmable tile
                    const tileWorldX = plotLeft + (tileX * 16) + 8;  // +8 to center in tile
                    const tileWorldY = plotTop + (displayTileY * 16) + 8;   // +8 to center in tile
                    
                    // Create white highlight box
                    this.farmableTileHighlight = this.add.rectangle(
                        tileWorldX, 
                        tileWorldY, 
                        16, 
                        16, 
                        0xffffff, 
                        0
                    ).setStrokeStyle(2, 0xffffff).setDepth(3);
                    
                    // Log tile detection (avoid spam by checking if different from last)
                    const clockwisePlotNumber = this.plotClockwiseMapping[plot.index - 1]; // Convert to clockwise numbering
                    const rowNumber = displayTileY === 0 ? 1 : (displayTileY === 2 ? 2 : 3); // Convert farmable rows to 1-3
                    const tileNumber = tileX + 1; // Convert 0-9 to 1-10
                    const currentTileId = `${clockwisePlotNumber},${rowNumber},${tileNumber}`;
                    
                    if (this.lastHighlightedTile !== currentTileId) {
                        //console.log(`Farmable tile detected: (${currentTileId})`);
                        this.lastHighlightedTile = currentTileId;
                    }
                    
                    break; // Only highlight one tile at a time
                }
            }
        }
        
        // Clear last highlighted tile if no tile is currently highlighted
        if (!this.farmableTileHighlight && this.lastHighlightedTile) {
            this.lastHighlightedTile = null;
        }
    }

    updateFarmplotSprites() {
        // Update farmplot sprites based on debug mode and unlock states
        this.farmPlots.forEach(plot => {
            // In debug mode, always show unlocked. Otherwise, show based on actual unlock state
            const shouldShowUnlocked = this.debugMode || plot.unlocked;
            const textureKey = shouldShowUnlocked ? 'farmplot_unlocked' : 'farmplot_locked';
            
            plot.sprite.setTexture(textureKey);
        });
    }

    getClickedFarmableTile(pointer) {
        if (!pointer) return null;
        
        // Convert screen coordinates to world coordinates
        const worldX = pointer.worldX;
        const worldY = pointer.worldY;
        
        // Check each farmplot for the clicked tile
        for (const plot of this.farmPlots) {
            // Skip locked plots unless debug mode is on
            if (!plot.unlocked && !this.debugMode) continue;
            
            // Calculate farmplot bounds (farmplots are 160x80, centered on position)
            const plotLeft = plot.position.x - 80;   // 160/2 = 80
            const plotRight = plot.position.x + 80;
            const plotTop = plot.position.y - 40;    // 80/2 = 40
            const plotBottom = plot.position.y + 40;
            
            // Check if click is within this farmplot
            if (worldX >= plotLeft && worldX <= plotRight && 
                worldY >= plotTop && worldY <= plotBottom) {
                
                // Calculate which 16x16 tile was clicked relative to the farmplot
                const relativeX = worldX - plotLeft;
                const relativeY = worldY - plotTop;
                
                const tileX = Math.floor(relativeX / 16);
                const tileY = Math.floor(relativeY / 16);
                
                // Check if this tile is in a farmable row
                // Farmable rows are at y positions 0, 2, and 4 (out of 0-4)
                const isFarmableRow = (tileY === 0 || tileY === 2 || tileY === 4);
                
                // Check if tile coordinates are valid
                const isValidTile = tileX >= 0 && tileX < 10 && tileY >= 0 && tileY <= 4;
                
                if (isFarmableRow && isValidTile) {
                    // Return tile information
                    return {
                        plotIndex: plot.index,
                        farmableRow: tileY / 2, // Convert to 0, 1, 2 for farmable rows
                        tileX: tileX
                    };
                }
            }
        }
        
        return null;
    }

    getCurrentFarmableTile() {
        // Returns the current farmable tile data if player is standing on one
        // Use tool detection position for consistency with highlighting
        const detectionPos = this.getToolDetectionPosition();
        const playerX = detectionPos.x;
        const playerY = detectionPos.y;
        
        for (const plot of this.farmPlots) {
            // Skip locked plots unless debug mode is on
            if (!plot.unlocked && !this.debugMode) continue;
            
            // Calculate farmplot bounds
            const plotLeft = plot.position.x - 80;
            const plotRight = plot.position.x + 80;
            const plotTop = plot.position.y - 40;
            const plotBottom = plot.position.y + 40;
            
            // Expand detection bounds slightly for better user experience
            const detectionBuffer = 12;
            if (playerX >= plotLeft && playerX <= plotRight && 
                playerY >= plotTop - detectionBuffer && playerY <= plotBottom + detectionBuffer) {
                
                // Calculate tile position
                const relativeX = playerX - plotLeft;
                const relativeY = playerY - plotTop;
                const tileX = Math.floor(relativeX / 16);
                const tileY = Math.floor(relativeY / 16);
                
                // Check if farmable
                const isFarmableRow = (tileY === 0 || tileY === 2 || tileY === 4);
                
                // Allow detection slightly outside bounds for better UX
                const isValidTile = tileX >= 0 && tileX < 10 && tileY >= -1 && tileY <= 5;
                
                if (isFarmableRow && isValidTile) {
                    // Clamp tileY to valid range
                    const clampedTileY = Math.max(0, Math.min(4, tileY));
                    
                    return {
                        plotIndex: plot.index,
                        tileX: tileX,
                        tileY: clampedTileY,
                        farmableRow: clampedTileY === 0 ? 0 : (clampedTileY === 2 ? 1 : 2), // Convert to 0-2 range
                        worldX: plotLeft + (tileX * 16) + 8,
                        worldY: plotTop + (clampedTileY * 16) + 8
                    };
                }
            }
        }
        
        return null; // No farmable tile found
    }

    update() {
        if (this.player && this.player.update) {
            this.player.update();
            
            // Check for player movement to dismiss mood dialogues
            this.checkPlayerMovementForMoodDialogue();
            
            // Update farmable tile highlighting only if player exists
            this.updateFarmableTileHighlight();
            
            // Check for produce pickup
            this.checkProducePickup();
        }
        
        // Check if player has left the chest zone
        if (this.chestCollisionZone && this.player) {
            const playerBounds = this.player.getBounds();
            const zoneBounds = this.chestCollisionZone.getBounds();
            
            const playerInZone = Phaser.Geom.Rectangle.Overlaps(playerBounds, zoneBounds);
            
            if (this.playerInChestZone && !playerInZone) {
                // Player just left the zone
                this.playerInChestZone = false;
                this.closeChest();
            }
        }
        
        // Check if player has left any plot sign zones
        this.plotSigns.forEach(plotSign => {
            if (plotSign.playerInZone && plotSign.interactionZone) {
                const playerBounds = this.player.getBounds();
                const zoneBounds = plotSign.interactionZone.getBounds();
                
                const playerInZone = Phaser.Geom.Rectangle.Overlaps(playerBounds, zoneBounds);
                
                if (!playerInZone) {
                    plotSign.playerInZone = false;
                    
                    // If this plot has an active dialogue, destroy it
                    if (this.activePlotDialogue && this.activeDialoguePlotIndex === plotSign.index) {
                        this.activePlotDialogue.destroy();
                        this.activePlotDialogue = null;
                        this.activeDialoguePlotIndex = null;
                    }
                }
            }
        });
    }
    
    checkProducePickup() {
        if (!this.player || this.produceItems.length === 0) return;
        
        const playerX = this.player.x;
        const playerY = this.player.y;
        
        // Debug logging for stuck items (only log every 2 seconds to avoid spam)
        if (!this.lastProduceDebugTime || Date.now() - this.lastProduceDebugTime > 2000) {
            const unpickableItems = this.produceItems.filter(p => !p.picked).length;
            if (unpickableItems > 0) {
                console.log(`🔍 PRODUCE DEBUG: ${unpickableItems} unpicked items remaining:`, 
                    this.produceItems.filter(p => !p.picked).map((p, i) => ({
                        index: i,
                        type: p.rewardType || 'crop',
                        id: p.rewardId || p.cropId,
                        position: { x: Math.round(p.sprite.x), y: Math.round(p.sprite.y) },
                        playerDistance: Math.round(Phaser.Math.Distance.Between(playerX, playerY, p.sprite.x, p.sprite.y))
                    }))
                );
            }
            this.lastProduceDebugTime = Date.now();
        }
        
        // Check each produce item for pickup
        for (let i = this.produceItems.length - 1; i >= 0; i--) {
            const produce = this.produceItems[i];
            
            if (produce.picked) continue; // Skip already picked items
            
            // Check if player is within 20 pixel pickup zone (increased from 4 for better UX)
            const distance = Phaser.Math.Distance.Between(
                playerX, playerY, 
                produce.sprite.x, produce.sprite.y
            );
            
            if (distance <= 20) {
                // Player is close enough, start pickup
                this.pickupProduce(produce, i);
            }
        }
    }
    
    pickupProduce(produce, index) {
        // Mark as picked to prevent multiple pickups
        produce.picked = true;
        
        // Play random pickup sound
        const pickupSounds = ['crop-pickup-1', 'crop-pickup-2'];
        const randomPickupSound = pickupSounds[Math.floor(Math.random() * pickupSounds.length)];
        this.sound.play(randomPickupSound, { volume: 0.4 });
        
        // Stop floating animation
        this.tweens.killTweensOf(produce.sprite);
        
        // Create suction animation toward player
        this.tweens.add({
            targets: produce.sprite,
            x: this.player.x,
            y: this.player.y,
            scaleX: 0.5,
            scaleY: 0.5,
            alpha: 0.7,
            duration: 200,
            ease: 'Power2',
            onComplete: () => {
                let inventorySuccess = false;
                
                // Add to inventory based on reward type
                if (produce.rewardType) {
                    // New reward system (crops and seedbags)
                    try {
                        this.addRewardToInventory(produce.rewardType, produce.rewardId);
                        inventorySuccess = true;
                    } catch (error) {
                        console.error(`❌ PICKUP FAILED: Error adding ${produce.rewardType} ${produce.rewardId} to inventory:`, error);
                    }
                } else {
                    // Legacy crop system
                    try {
                        this.addProduceToInventory(produce.cropId);
                        inventorySuccess = true;
                    } catch (error) {
                        console.error(`❌ PICKUP FAILED: Error adding crop ${produce.cropId} to inventory:`, error);
                    }
                }
                
                // Always clean up sprite and array entry, even if inventory add failed
                if (produce.sprite && produce.sprite.destroy) {
                    produce.sprite.destroy();
                }
                
                // Safely remove from produce items array
                if (index >= 0 && index < this.produceItems.length && this.produceItems[index] === produce) {
                    this.produceItems.splice(index, 1);
                } else {
                    console.warn(`⚠️ PICKUP WARNING: Could not find item at index ${index} for cleanup`);
                    // Try to find and remove by sprite reference
                    const spriteIndex = this.produceItems.findIndex(p => p.sprite === produce.sprite);
                    if (spriteIndex !== -1) {
                        this.produceItems.splice(spriteIndex, 1);
                    }
                }
                
            }
        });
    }
    
    addProduceToInventory(cropId) {
        // Get the consumable item data
        const itemData = itemManager.getItemData(`Consumable_${cropId}`);
        if (!itemData) {
            console.warn(`Could not find item data for Consumable_${cropId}`);
            return;
        }
        
        // Add to inventory using the inventory manager with correct parameter format
        inventoryManager.addItem({
            itemCode: cropId, // Just the numeric ID
            categoryCode: 0,  // Consumables category
            quantity: 1
        });
        
    }
    
    addRewardToInventory(rewardType, rewardId) {
        if (rewardType === 'crop') {
            // Add crop as consumable
            const itemData = itemManager.getItemData(`Consumable_${rewardId}`);
            if (!itemData) {
                console.warn(`Could not find crop data for Consumable_${rewardId}`);
                return;
            }
            
            inventoryManager.addItem({
                itemCode: rewardId,
                categoryCode: 0, // Consumables category
                quantity: 1
            });
                        
        } else if (rewardType === 'seedbag') {
            // Add seedbag 
            const seedData = itemManager.getItemData(`Seeds_${rewardId}`);
            if (!seedData) {
                return;
            }
            
            inventoryManager.addItem({
                itemCode: rewardId,
                categoryCode: 1, // Seeds category
                quantity: 1
            });
            
        }
    }
    
    addSparkleAnimation(plotIndex, farmableRow, tileIndex) {
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        // Calculate sparkle position (center of the farmable tile)
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8; // Center of 16x16 tile
        const worldY = plotTop + (tileY * 16) + 8;  // Center of 16x16 tile
        
        // Create sparkle sprite
        const sparkleSprite = this.add.sprite(worldX, worldY, 'sparkle');
        sparkleSprite.setDepth(2.9); // Above all crop sprites
        sparkleSprite.setOrigin(0.5, 0.5);
        sparkleSprite.setAlpha(1.0); // 100% opacity as requested
        
        // Play sparkle animation
        sparkleSprite.play('sparkle');
        
        // Store sparkle data with tile reference
        const sparkleData = {
            sprite: sparkleSprite,
            plotIndex: plotIndex,
            farmableRow: farmableRow,
            tileIndex: tileIndex,
            type: 'regular'
        };
        
        this.sparkleSprites.push(sparkleData);
    
    }
    
    removeSparkleAnimation(plotIndex, farmableRow, tileIndex) {
        // Find and remove all sparkle sprites for this tile (both regular and mutant)
        for (let i = this.sparkleSprites.length - 1; i >= 0; i--) {
            const sparkleData = this.sparkleSprites[i];
            
            // Check if this sparkle matches the tile position
            if (sparkleData.plotIndex === plotIndex && 
                sparkleData.farmableRow === farmableRow && 
                sparkleData.tileIndex === tileIndex) {
                
                sparkleData.sprite.destroy();
                this.sparkleSprites.splice(i, 1);
                
                const sparkleType = sparkleData.type === 'mutant' ? 'mutant ' : '';
            }
        }
     }
     
     createSparkleAnimations() {
         // Clear existing sparkle sprites
         this.sparkleSprites.forEach(sparkle => {
             if (sparkle.sprite) {
                 sparkle.sprite.destroy();
             }
         });
         this.sparkleSprites = [];
         
         // Create sparkle animations for tiles that have harvestable crops (phase 7)
         this.tileStates.forEach((tileData, key) => {
             if (tileData.phase === 7 && tileData.cropId && tileData.cropId > 0) {
                 const [plot, row, tile] = key.split('_').map(Number);
                 this.addSparkleAnimation(plot, row, tile);
             }
         });
         
     }

    checkPlayerMovementForMoodDialogue() {
        if (!this.player || !this.activeMoodDialogue) {
            return;
        }
        
        const currentPos = { x: this.player.x, y: this.player.y };
        const threshold = 2; // Minimum distance to consider "movement"
        
        const distanceMoved = Phaser.Math.Distance.Between(
            this.lastPlayerPosition.x, 
            this.lastPlayerPosition.y, 
            currentPos.x, 
            currentPos.y
        );
        
        if (distanceMoved > threshold) {
            // Player has moved, dismiss mood dialogue
            this.activeMoodDialogue.destroy();
            this.activeMoodDialogue = null;
        }
        
        // Update last position
        this.lastPlayerPosition.x = currentPos.x;
        this.lastPlayerPosition.y = currentPos.y;
    }

    cleanup() {

        // Stop farm ambiance
        if (this.sounds && this.sounds.farm_ambiance) {
            this.sounds.farm_ambiance.stop();
        }

        // Clean up debug visuals
        this.removeDebugBorders();
        this.removeDebugGrid();

        // Clean up farmable tile highlight
        if (this.farmableTileHighlight) {
            this.farmableTileHighlight.destroy();
            this.farmableTileHighlight = null;
        }
        this.lastHighlightedTile = null;

        // Clean up farm feature sprites
        if (this.farmPlots) {
            this.farmPlots.forEach(plot => {
                if (plot.sprite && plot.sprite.destroy) {
                    plot.sprite.destroy();
                }
            });
            this.farmPlots = [];
        }

        if (this.plotSigns) {
            this.plotSigns.forEach(sign => {
                if (sign.sprite && sign.sprite.destroy) {
                    sign.sprite.destroy();
                }
                if (sign.collisionZone && sign.collisionZone.destroy) {
                    sign.collisionZone.destroy();
                }
                if (sign.interactionZone && sign.interactionZone.destroy) {
                    sign.interactionZone.destroy();
                }
            });
            this.plotSigns = [];
        }

        if (this.dungeonFence && this.dungeonFence.destroy) {
            this.dungeonFence.destroy();
            this.dungeonFence = null;
        }

        if (this.chickenCoop && this.chickenCoop.destroy) {
            this.chickenCoop.destroy();
            this.chickenCoop = null;
        }

        if (this.chest && this.chest.destroy) {
            this.chest.destroy();
            this.chest = null;
        }

        if (this.collisionMap && this.collisionMap.destroy) {
            this.collisionMap.destroy();
            this.collisionMap = null;
        }
        
        // Clean up sprite arrays safely
        if (this.rawTileSprites) {
            this.rawTileSprites.forEach(sprite => {
                if (sprite && sprite.destroy) sprite.destroy();
            });
            this.rawTileSprites = [];
        }
        
        if (this.wateredTileSprites) {
            this.wateredTileSprites.forEach(sprite => {
                if (sprite && sprite.destroy) sprite.destroy();
            });
            this.wateredTileSprites = [];
        }
        
        if (this.plantedSeedSprites) {
            this.plantedSeedSprites.forEach(sprite => {
                if (sprite && sprite.destroy) sprite.destroy();
            });
            this.plantedSeedSprites = [];
        }
        
        if (this.deadCropSprites) {
            this.deadCropSprites.forEach(sprite => {
                if (sprite && sprite.destroy) sprite.destroy();
            });
            this.deadCropSprites = [];
        }
        
        // Clean up produce items and their tweens
        if (this.produceItems) {
            this.produceItems.forEach(produce => {
                if (produce.sprite) {
                    this.tweens.killTweensOf(produce.sprite);
                    if (produce.sprite.destroy) produce.sprite.destroy();
                }
            });
            this.produceItems = [];
        }
        
        // Clean up sparkle sprites
        if (this.sparkleSprites) {
            this.sparkleSprites.forEach(sparkle => {
                if (sparkle.sprite && sparkle.sprite.destroy) {
                    sparkle.sprite.destroy();
                }
            });
            this.sparkleSprites = [];
        }
        
        // Clean up growth timers
        if (this.growthTimers) {
            this.growthTimers.forEach((timerId, key) => {
                if (timerId) clearTimeout(timerId);
            });
            this.growthTimers.clear();
        }
        
        // Clean up physics collision bodies safely
        if (this.collisionBodies) {
            try {
                // Remove all physics world references first
                if (this.physics && this.physics.world && this.player) {
                    this.physics.world.removeCollider(this.player, this.collisionBodies);
                }
                
                // Clear and destroy the group safely
                if (this.collisionBodies.children && typeof this.collisionBodies.children.size !== 'undefined') {
                    this.collisionBodies.clear(true, true); // Remove and destroy all children
                }
                this.collisionBodies.destroy();
                this.collisionBodies = null;
            } catch (error) {
                console.warn('Error cleaning up collision bodies:', error);
                this.collisionBodies = null;
            }
        }
        
        // Clean up active plot dialogue
        if (this.activePlotDialogue) {
            this.activePlotDialogue.destroy();
            this.activePlotDialogue = null;
            this.activeDialoguePlotIndex = null;
        }
        
        // Clean up active mood dialogue
        if (this.activeMoodDialogue) {
            this.activeMoodDialogue.destroy();
            this.activeMoodDialogue = null;
        }

        // Plot sign cleanup is now handled earlier in the cleanup() method

        // Clean up collision bodies safely  
        if (this.collisionBodies) {
            try {
                if (this.collisionBodies.children && this.collisionBodies.children.size > 0) {
                    this.collisionBodies.clear(true, true); // Remove and destroy all children
                }
                this.collisionBodies.destroy();
                this.collisionBodies = null;
            } catch (error) {
                console.warn('Error cleaning up collision bodies:', error);
            }
        }
        
        // Clean up scene transition bodies safely
        if (this.sceneTransitionBodies) {
            try {
                // Remove all physics world references first
                if (this.physics && this.physics.world && this.player) {
                    this.physics.world.removeOverlap(this.player, this.sceneTransitionBodies);
                }
                
                // Clear and destroy the group safely
                if (this.sceneTransitionBodies.children && typeof this.sceneTransitionBodies.children.size !== 'undefined') {
                    this.sceneTransitionBodies.clear(true, true); // Remove and destroy all children  
                }
                this.sceneTransitionBodies.destroy();
                this.sceneTransitionBodies = null;
            } catch (error) {
                //console.warn('Error cleaning up scene transition bodies:', error);
                this.sceneTransitionBodies = null;
            }
        }
        
        // Clean up chest collision zone
        if (this.chestCollisionZone) {
            try {
                // Remove overlap detection first
                if (this.physics && this.physics.world && this.player) {
                    this.physics.world.removeOverlap(this.player, this.chestCollisionZone);
                }
                this.chestCollisionZone.destroy();
                this.chestCollisionZone = null;
            } catch (error) {
                //console.warn('Error cleaning up chest collision zone:', error);
                this.chestCollisionZone = null;
            }
        }
        
        // Clean up player safely
        if (this.player) {
            try {
                // Destroy the player sprite completely (this also removes its physics body)
                this.player.destroy();
                this.player = null;
                //console.log('✅ Player cleanup completed');
            } catch (error) {
                //console.warn('Error cleaning up player:', error);
                this.player = null;
            }
        }
        
        // Clean up debug tracking
        if (this.debugMutantCrops) {
            this.debugMutantCrops.clear();
        }
        
        // Clean up other resources
        if (this.cameras.main) {
            this.cameras.main.stopFollow();
        }
    }
    
    setupChest() {
        // Try loading static chest image directly
        this.load.image('chest_stationary', '/sprites/chest_stationary.png');

        this.load.once('complete', () => {
            if (this.textures.exists('chest_stationary')) {
                this.chest = this.add.image(432, 32, 'chest_stationary');
                this.chest.setDepth(3);
                this.chest.setOrigin(0.5, 0.5);
                this.chest.setScale(2);
            } else {
                //console.warn('⚠️ Chest image failed, using fallback');
                this.createFallbackChest();
            }
        });

        this.load.once('loaderror', (file) => {
            //console.error('❌ Failed to load chest:', file.key, file.url);
            this.createFallbackChest();
        });

        this.load.start();
        
        // Track chest state
        this.chestOpen = false;
        
        // Create collision rectangle for chest interaction
        // 48x48 zone centered on the chest
        this.chestCollisionZone = this.add.zone(432, 32, 48, 48);
        this.physics.world.enable(this.chestCollisionZone);
        this.chestCollisionZone.body.setImmovable(true);
        
        // Set up collision detection with player
        this.physics.add.overlap(this.player, this.chestCollisionZone, () => {
            this.handleChestApproach();
        });
        
        // Track if player was in zone last frame (for exit detection)
        this.playerInChestZone = false;
        
        // Track currently open plot sign dialogue
        this.activePlotDialogue = null;
        this.activeDialoguePlotIndex = null;
        
        // Track mood dialogue for movement dismissal
        this.activeMoodDialogue = null;
        this.lastPlayerPosition = { x: 0, y: 0 };
    }

    createFallbackChest() {
        // Create a simple brown rectangle as fallback chest
        this.chest = this.add.rectangle(432, 32, 24, 24, 0x8B4513);
        this.chest.setDepth(3);
        this.chest.setOrigin(0.5, 0.5);
        this.chest.setScale(2);
        //console.log('✅ Fallback chest rectangle created');
    }

    handlePlotSignApproach(plotIndex) {
        const plotSign = this.plotSigns.find(sign => sign.index === plotIndex);
        if (!plotSign || plotSign.playerInZone) {
            return;
        }
        
        // Don't create new dialogue if one is already active for this plot
        if (this.activePlotDialogue && this.activeDialoguePlotIndex === plotIndex) {
            return;
        }
        
        plotSign.playerInZone = true;
        
        // Close any existing plot dialogue first
        if (this.activePlotDialogue) {
            this.activePlotDialogue.destroy();
            this.activePlotDialogue = null;
            this.activeDialoguePlotIndex = null;
        }
        
        // Show option dialogue for plot purchase
        const plotCost = this.getPlotCost(plotIndex);
        const dialogue = DialogueWindow.createCameraRelative(this, 10, 10);
        
        // Store dialogue reference
        this.activePlotDialogue = dialogue;
        this.activeDialoguePlotIndex = plotIndex;
        
        dialogue.setOptionDialogue(
            `Farm Plot #${plotIndex} costs ¥${plotCost} to unlock. Proceed?`,
            'question',
            () => this.handlePlotPurchaseAccept(plotIndex, plotCost),
            () => this.handlePlotPurchaseDecline(plotIndex)
        );
    }
    
    getPlotCost(plotIndex) {
        // Define plot costs (could be stored in database later)
        const plotCosts = {
            1: 1000,
            2: 5000,
            3: 20000,
            4: 100000
        };
        return plotCosts[plotIndex] || 1000;
    }

    // showCryptoCrashFarmDialogue(){
    //     // WINDOWS 98 POPUP - increased delay to ensure all assets are loaded
    //     this.time.delayedCall(2000, () => {
    //         this.game.events.emit('forceCloseMenus');
    //         const popup = new Win98Window(
    //             this,
    //             130,
    //             95,
    //             'win98window',
    //             'You need money to upgrade the farm... You heard about a new coin called $CHI. Ape it?'
    //         );

    //         popup.setOkayCallback(() => {
    //             popup.destroy();
    //             this.sound.play('lgg', { volume: 0.4 });
    //             // Debug: Log before emitting event
    //             console.log('Emitting showCryptoCrash event from farm plot purchase');
    //             // Emit global event that Game component can listen to
    //             this.game.events.emit('showCryptoCrash');
    //         });
            
    //         popup.setCloseCallback(() => {
    //             popup.destroy();
    //             popup.messageText.destroy();
    //             // DIALOGUE TEST
    //             this.time.delayedCall(300, () => {
    //                 const dialogue = new DialogueWindow(this, 10, 10);
    //                 dialogue.setMultiDialogue([
    //                     ["If you want to upgrade the farm, we could  always sell some fish...", "basic"],
    //                     ["after I you feed me some, of course.", "happy"]
    //                 ]);  
    //             });
    //         }, [], this); 
    //     });
    // }
    
    async handlePlotPurchaseAccept(plotIndex, cost) {
        // Clear the tracked dialogue
        this.activePlotDialogue = null;
        this.activeDialoguePlotIndex = null;
        const dialogue = DialogueWindow.createCameraRelative(this, 10, 10);

        // Player doesn't have enough money
        const currentStats = this.statsManager.getStats();
        if (currentStats.money < cost) {
            dialogue.setDialogue("You don't have enough money for this plot.", "sad");
            // this.showCryptoCrashFarmDialogue();
            return;
        }

        // Show success message
        dialogue.setDialogue(`Plot #${plotIndex} purchased for ¥${cost}! You can now farm here.`, 'complete');
        await this.statsManager.updateMoney(cost, 'subtract');
        
        // Update local state and remove the plot sign
        this.unlockStates[`farmplot_${plotIndex}_unlocked`] = true;
        this.removePlotSign(plotIndex);

        // Create update object with proper format
        const updateData = { [`farmplot_${plotIndex}_unlocked`]: true };

        // Update user options record in database
        try {
            await window.gameService.updateUserOptions(updateData);
            console.log(`Successfully unlocked farm plot ${plotIndex} in database`);
        } catch (error) {
            console.error('Error updating farm plot unlock state:', error);
        }
    }
    
    handlePlotPurchaseDecline(plotIndex) {
        // Clear the tracked dialogue
        this.activePlotDialogue = null;
        this.activeDialoguePlotIndex = null;
        
        const dialogue = DialogueWindow.createCameraRelative(this, 10, 10);
        dialogue.setDialogue('Maybe another time!', 'basic');
    }
    
    removePlotSign(plotIndex) {
        const plotSignIndex = this.plotSigns.findIndex(sign => sign.index === plotIndex);
        if (plotSignIndex !== -1) {
            const plotSign = this.plotSigns[plotSignIndex];
            
            // Clear any active dialogue for this plot
            if (this.activePlotDialogue && this.activeDialoguePlotIndex === plotIndex) {
                this.activePlotDialogue.destroy();
                this.activePlotDialogue = null;
                this.activeDialoguePlotIndex = null;
            }
            
            // Destroy sprite and zones
            plotSign.sprite.destroy();
            if (plotSign.collisionZone) plotSign.collisionZone.destroy();
            if (plotSign.interactionZone) plotSign.interactionZone.destroy();
            
            // Remove from array
            this.plotSigns.splice(plotSignIndex, 1);
        }
        
        // Update farmplot sprite to unlocked version
        this.updateFarmplotSprites();
    }
    
    handleChestApproach() {
        if (!this.playerInChestZone && !this.chestOpen) {
            // Player just entered the zone and chest is closed
            this.playerInChestZone = true;
            this.openChest();
        } else if (!this.playerInChestZone && this.chestOpen) {
            // Player re-entered while chest was already open
            this.playerInChestZone = true;
        }
    }
    
    openChest() {
        if (this.chestOpen) return;

        if (!this.chest) {
            console.error('❌ Cannot open chest - chest sprite not initialized');
            return;
        }

        this.chestOpen = true;

        // Play opening sound
        this.sound.play('chest_open', { volume: 0.4 });

        // Open vendor immediately (no animation)
        this.openVendor();
    }
    
    openVendor() {
        // Define vendor data - pricing and requirements come from crop data now
        const vendorData = [
            {
                cropId: 39, // Potato crop ID from items.json
                quantity: -1 // Unlimited stock
            },
            {
                cropId: 36, // Carrot crop ID from items.json  
                quantity: -1 // Unlimited stock
            },
            {
                cropId: 38, // Onion crop ID from items.json
                quantity: -1 // Unlimited stock
            },
            {
                cropId: 37, // Corn crop ID from items.json
                quantity: 5 // Limited stock to test farming level requirement
            }
        ];
        
        // Pause this scene and start vendor scene
        this.scene.pause();
        this.scene.launch('VendorScene', { vendorData: vendorData });
    }
    
    closeChest() {
        if (!this.chestOpen) return;
        this.chestOpen = false;

        // Play closing sound
        this.sound.play('chest_close', { volume: 0.4 });

        // No animation needed - chest stays static
    }

    // ========= GROWTH PROGRESSION SYSTEM =========
    
    startGrowthProgression(plotIndex, farmableRow, tileIndex) {
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        const tileState = this.tileStates.get(key);
        
        if (!tileState || tileState.phase !== 2) {
            console.warn('Cannot start growth: invalid state or not seeds');
            return;
        }
        
        // Clear any existing timer for this tile
        this.clearGrowthTimer(key);
        
        // Update state with growth start time
        this.tileStates.set(key, {
            ...tileState,
            growthStarted: Date.now(),
            lastPhaseAdvancement: Date.now()
        });
        
        // Start 30-second timer for first growth phase (seeds → growth 1)
        const timerId = setTimeout(() => {
            this.advanceGrowthPhase(plotIndex, farmableRow, tileIndex);
        }, this.GROWTH_PHASE_DURATION);
        
        this.growthTimers.set(key, timerId);
        //console.log(`Growth timer started for ${key}: seeds will advance to growth 1 in 30 seconds`);
    }
    
    async advanceGrowthPhase(plotIndex, farmableRow, tileIndex) {
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        const tileState = this.tileStates.get(key);
        
        if (!tileState || tileState.phase < 2 || tileState.phase >= 7) {
            //console.warn('Cannot advance growth: invalid phase');
            return;
        }
        
        const newPhase = tileState.phase + 1;
        const currentTime = Date.now();
        
        // Update local state
        this.tileStates.set(key, {
            ...tileState,
            phase: newPhase,
            lastPhaseAdvancement: currentTime,
            // Reset watered state on phase transition
            wateredThisPhase: false
        });
        
        // Update database with new phase
        try {
            const gameService = window.gameService;
            if (gameService) {
                await gameService.advanceGrowth(plotIndex, farmableRow, tileIndex, newPhase);
                //console.log(`Database updated: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex} advanced to phase ${newPhase}`);
            }
        } catch (error) {
            console.error('Error updating database with growth advancement:', error);
        }
        
        // Update sprite to new growth phase
        this.updateCropSprite(plotIndex, farmableRow, tileIndex, newPhase);
        
        // Remove watered tile sprite (reset watered state)
        this.removeWateredTileSprite(plotIndex, farmableRow, tileIndex);
        
       // console.log(`Crop advanced: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex} → Phase ${newPhase}`);
        
        // Set up next growth phase timer (if not fully mature)
        if (newPhase < 7) {
            const timerId = setTimeout(() => {
                this.advanceGrowthPhase(plotIndex, farmableRow, tileIndex);
            }, this.GROWTH_PHASE_DURATION);
            
            this.growthTimers.set(key, timerId);
            //console.log(`Next growth timer set for ${key}: phase ${newPhase} → ${newPhase + 1} in 30 seconds`);
        } else if (newPhase === 7) {
            // Crop is ready for harvest - add appropriate sparkle animation
            if (this.isMutantCrop(plotIndex, farmableRow, tileIndex)) {
                this.addMutantSparkleAnimation(plotIndex, farmableRow, tileIndex);
                //console.log(`🔥 MUTANT CROP ready for harvest at Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}!`);
            } else {
                this.addSparkleAnimation(plotIndex, farmableRow, tileIndex);
            }
            // Start harvest deadline timer
            this.startHarvestDeadline(plotIndex, farmableRow, tileIndex);
        }
    }
    
    updateCropSprite(plotIndex, farmableRow, tileIndex, newPhase) {
        // Remove old planted seed sprite
        this.removePlantedSeedSprite(plotIndex, farmableRow, tileIndex);
        
        // Get current tile state to get crop ID
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        const tileState = this.tileStates.get(key);
        
        if (!tileState || !tileState.cropId) {
            console.warn('Cannot update crop sprite: missing crop data');
            return;
        }
        
        // Create new sprite with updated phase
        const sprite = this.createPlantedSeedSprite(plotIndex, farmableRow, tileIndex, tileState.cropId, newPhase);
        if (sprite) {
            this.plantedSeedSprites.push(sprite);
            //console.log(`Updated crop sprite to phase ${newPhase}`);
        }
    }
    
    startHarvestDeadline(plotIndex, farmableRow, tileIndex) {
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        
        // Set 15-minute timer for harvest deadline
        const timerId = setTimeout(() => {
            this.witherMatureCrop(plotIndex, farmableRow, tileIndex);
        }, this.HARVEST_DEADLINE);
        
        this.growthTimers.set(key + '_harvest', timerId);
        //console.log(`Harvest deadline set for ${key}: 15 minutes to harvest or crop will wither`);
    }
    
    async witherMatureCrop(plotIndex, farmableRow, tileIndex) {
        const key = this.getTileKey(plotIndex, farmableRow, tileIndex);
        const tileState = this.tileStates.get(key);
        
        if (!tileState || tileState.phase !== 7) {
            console.warn('Cannot wither crop: not mature or already processed');
            return;
        }
        
        // Update to withered state (phase 10)
        this.tileStates.set(key, {
            ...tileState,
            phase: 10,
            witheredAt: Date.now()
        });
        
        // Update database
        try {
            const gameService = window.gameService;
            if (gameService) {
                await gameService.advanceGrowth(plotIndex, farmableRow, tileIndex, 10);
                //console.log(`Database updated: Crop withered at Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}`);
            }
        } catch (error) {
            console.error('Error updating database with withered crop:', error);
        }
        
        // Update sprite to dead crop
        this.removePlantedSeedSprite(plotIndex, farmableRow, tileIndex);
        const deadSprite = this.createDeadCropSprite(plotIndex, farmableRow, tileIndex, tileState.cropId);
        if (deadSprite) {
            this.deadCropSprites.push(deadSprite);
        }
        
        //console.log(`Mature crop withered: Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}`);
    }
    
    clearGrowthTimer(key) {
        // Clear growth progression timer
        if (this.growthTimers.has(key)) {
            clearTimeout(this.growthTimers.get(key));
            this.growthTimers.delete(key);
        }
        
        // Clear harvest deadline timer
        const harvestKey = key + '_harvest';
        if (this.growthTimers.has(harvestKey)) {
            clearTimeout(this.growthTimers.get(harvestKey));
            this.growthTimers.delete(harvestKey);
        }
    }
    
    calculateOfflineProgression() {
        const currentTime = Date.now();
        const updates = [];
        
        this.tileStates.forEach((tileState, key) => {
            const [plot, row, tile] = key.split('_').map(Number);
            
            // Skip if no growth started or already mature/dead
            if (!tileState.growthStarted || tileState.phase < 2 || tileState.phase >= 7) return;
            
            const timeSinceLastPhase = currentTime - (tileState.lastPhaseAdvancement || tileState.growthStarted);
            const phasesPassed = Math.floor(timeSinceLastPhase / this.GROWTH_PHASE_DURATION);
            
            if (phasesPassed > 0) {
                const newPhase = Math.min(tileState.phase + phasesPassed, 7);
                const timeAtMaturity = tileState.lastPhaseAdvancement + ((7 - tileState.phase) * this.GROWTH_PHASE_DURATION);
                
                // Check if crop should be withered (15 minutes after reaching maturity)
                let finalPhase = newPhase;
                if (newPhase === 7 && currentTime > (timeAtMaturity + this.HARVEST_DEADLINE)) {
                    finalPhase = 10; // Withered
                }
                
                updates.push({
                    key,
                    plot, row, tile,
                    oldPhase: tileState.phase,
                    newPhase: finalPhase,
                    cropId: tileState.cropId
                });
            }
        });
        
        return updates;
    }
    
    async applyOfflineProgression(updates) {
        //console.log(`Applying offline progression for ${updates.length} crops`);
        
        for (const update of updates) {
            // Update local state
            const tileState = this.tileStates.get(update.key);
            this.tileStates.set(update.key, {
                ...tileState,
                phase: update.newPhase,
                lastPhaseAdvancement: Date.now()
            });
            
            // Update database
            try {
                const gameService = window.gameService;
                if (gameService) {
                    await gameService.advanceGrowth(update.plot, update.row, update.tile, update.newPhase);
                }
            } catch (error) {
                console.error('Error updating database with offline progression:', error);
            }
            
            // Update sprites
            if (update.newPhase === 10) {
                // Dead crop
                this.removePlantedSeedSprite(update.plot, update.row, update.tile);
                const deadSprite = this.createDeadCropSprite(update.plot, update.row, update.tile, update.cropId);
                if (deadSprite) this.deadCropSprites.push(deadSprite);
            } else {
                // Living crop
                this.updateCropSprite(update.plot, update.row, update.tile, update.newPhase);
                
                // Start appropriate timers
                if (update.newPhase < 7) {
                    const remainingTime = this.GROWTH_PHASE_DURATION - ((Date.now() - tileState.lastPhaseAdvancement) % this.GROWTH_PHASE_DURATION);
                    setTimeout(() => {
                        this.advanceGrowthPhase(update.plot, update.row, update.tile);
                    }, remainingTime);
                } else if (update.newPhase === 7) {
                    // Crop reached harvestable stage - add sparkle animation
                    this.addSparkleAnimation(update.plot, update.row, update.tile);
                    this.startHarvestDeadline(update.plot, update.row, update.tile);
                }
            }
            
            //console.log(`Offline progression: Plot ${update.plot}, Row ${update.row}, Tile ${update.tile}: Phase ${update.oldPhase} → ${update.newPhase}`);
        }
    }
    
    async consumeSeed(cropId) {
        // Find the seed item that corresponds to this crop
        // We need to search for the seed item that has cropId matching our crop
        const inventory = inventoryManager.getInventory();
        const seedItem = inventory.find(item => {
            if (item.itemCode.startsWith('Seeds_')) {
                const seedData = itemManager.getItemData(item.itemCode);
                return seedData && seedData.cropId === cropId;
            }
            return false;
        });
        
        if (!seedItem) {
            console.warn(`No seed found in inventory for cropId ${cropId}`);
            return;
        }
        
        const seedItemCode = seedItem.itemCode;
        
        // Use inventoryManager to consume the seed
        const success = inventoryManager.removeItem(seedItemCode, 1);
        if (!success) {
            console.warn(`Failed to consume seed ${seedItemCode} - not enough in inventory`);
            return;
        }
        
        // Force immediate sync to database
        try {
            await inventoryManager.forceSync();
            //console.log('Inventory changes synced to database');
        } catch (error) {
            console.error('Failed to sync inventory to database:', error);
        }
        
        // Get new quantity for UI updates
        const updatedInventory = inventoryManager.getInventory();
        const updatedSeedItem = updatedInventory.find(item => item.itemCode === seedItemCode);
        const newQuantity = updatedSeedItem ? updatedSeedItem.quantity : 0;
        
        //console.log(`Consumed 1 ${seedItemCode} (cropId: ${cropId}) - ${newQuantity} remaining`);
        
        // Update ToolUI quantity display and auto-unequip if needed
        if (this.toolUI) {
            //console.log(`Updating ToolUI quantity for ${seedItemCode}: ${newQuantity}`);
            this.toolUI.updateSeedQuantity(seedItemCode, newQuantity);
        } else {
            console.warn('ToolUI not found when trying to update seed quantity');
        }
        
        // Update SeedbagUI quantity display if it's open
        const seedbagUI = this.seedbagUI;
        if (seedbagUI) {
            seedbagUI.updateSeedQuantities();
        }
    }
    
    restartGrowthTimers() {
        this.tileStates.forEach((tileState, key) => {
            const [plot, row, tile] = key.split('_').map(Number);
            
            // Only restart timers for crops that have started growing but aren't mature/dead
            if (tileState.growthStarted && tileState.phase >= 2 && tileState.phase < 7) {
                const timeSinceLastPhase = Date.now() - (tileState.lastPhaseAdvancement || tileState.growthStarted);
                const remainingTime = Math.max(0, this.GROWTH_PHASE_DURATION - (timeSinceLastPhase % this.GROWTH_PHASE_DURATION));
                
                if (remainingTime > 0) {
                    const timerId = setTimeout(() => {
                        this.advanceGrowthPhase(plot, row, tile);
                    }, remainingTime);
                    
                    this.growthTimers.set(key, timerId);
                    //console.log(`Restarted growth timer for ${key}: ${remainingTime}ms remaining for next phase`);
                }
            } else if (tileState.phase === 7) {
                // Restart harvest deadline timer for mature crops
                this.startHarvestDeadline(plot, row, tile);
            }
        });
    }
    
    removeWateredTileSprite(plotIndex, farmableRow, tileIndex) {
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        
        const worldX = plotLeft + (tileX * 16) + 8;
        const worldY = plotTop + (tileY * 16) + 8;
        
        // Find and remove the watered sprite at this position
        const spriteIndex = this.wateredTileSprites.findIndex(sprite => {
            return Math.abs(sprite.x - worldX) < 1 && Math.abs(sprite.y - worldY) < 1;
        });
        
        if (spriteIndex !== -1) {
            this.wateredTileSprites[spriteIndex].destroy();
            this.wateredTileSprites.splice(spriteIndex, 1);
            //console.log('Removed watered tile sprite (phase transition)');
        }
    }
    
    // Mutant crop and harvest reward system
    isMutantCrop(plotIndex, farmableRow, tileIndex) {
        const tileKey = this.getTileKey(plotIndex, farmableRow, tileIndex);
        
        // Check if this crop was debug-converted to mutant first
        if (this.debugMutantCrops.has(tileKey)) {
            return true;
        }
        
        // Check if this specific tile has a mutant crop (0.4% chance = 1/250)
        // Use tile key as seed for consistent randomness per tile
        const seed = this.hashString(tileKey);
        const random = this.seededRandom(seed);
        
        return random < 0.004; // 0.4% chance (1/250)
    }
    
    hashString(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash);
    }
    
    seededRandom(seed) {
        const x = Math.sin(seed) * 10000;
        return x - Math.floor(x);
    }
    
    calculateSeedbagReward() {
        // Calculate number of seedbags to reward based on probabilities
        const roll = Math.random() * 100;
        
        if (roll < 2.0) return 3;        // 2% chance for 3 seedbags
        if (roll < 12.0) return 2;       // 10% chance for 2 seedbags
        if (roll < 92.0) return 1;       // 80% chance for 1 seedbag
        return 0;                        // 8% chance for 0 seedbags
    }
    
    getRandomCropId() {
        // Get random crop from available crops (34-48 based on items.json)
        // Crops: 34=Beet, 35=Cabbage, 36=Carrot, 37=Corn, 38=Onion, 39=Potato, 
        //        40=Pea, 41=Beans, 42=Tomato, 43=Wheat, 44=Cucumber, 45=Spinach,
        //        46=Strawberry, 47=Blueberry, 48=Pumpkin
        const cropIds = [34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48];
        return cropIds[Math.floor(Math.random() * cropIds.length)];
    }
    
    getRandomSeedId() {
        // Get random seed from available seeds (1-16 based on Seeds section in items.json)
        // Seeds: 1=Onion, 2=Carrot, 3=Potato, 4=Corn, 5=Beet, 6=Cabbage, 7=Pea, 8=Beans,
        //        9=Tomato, 10=Wheat, 11=Cucumber, 12=Spinach, 13=Strawberry, 14=Blueberry,
        //        15=Pumpkin, 16=Broccoli
        const seedIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
        return seedIds[Math.floor(Math.random() * seedIds.length)];
    }
    
    getSeedIdFromCropId(cropId) {
        // Map crop IDs to their corresponding seed IDs
        // Based on the cropId field in each seed's data
        const cropToSeedMap = {
            34: 5,  // Beet crop → Beet seeds
            35: 6,  // Cabbage crop → Cabbage seeds  
            36: 2,  // Carrot crop → Carrot seeds
            37: 4,  // Corn crop → Corn seeds
            38: 1,  // Onion crop → Onion seeds
            39: 3,  // Potato crop → Potato seeds
            40: 7,  // Pea crop → Pea seeds
            41: 8,  // Beans crop → Bean seeds
            42: 9,  // Tomato crop → Tomato seeds
            43: 10, // Wheat crop → Wheat seeds
            44: 11, // Cucumber crop → Cucumber seeds
            45: 12, // Spinach crop → Spinach seeds
            46: 13, // Strawberry crop → Strawberry seeds
            47: 14, // Blueberry crop → Blueberry seeds
            48: 15, // Pumpkin crop → Pumpkin seeds
            49: 16  // Broccoli crop → Broccoli seeds
        };
        
        return cropToSeedMap[cropId] || null;
    }
    
    async spawnHarvestRewards(plotIndex, farmableRow, tileIndex, cropId, isMutant = false) {
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot) return;
        
        // Calculate spawn origin position
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        const originX = plotLeft + (tileX * 16) + 8;
        const originY = plotTop + (tileY * 16) + 8 - 8; // Match crop position
        
        // Always spawn the main crop first
        this.spawnProduceItem(plotIndex, farmableRow, tileIndex, cropId);
        
        let rewards = [];
        
        if (isMutant) {
            //console.log('🔥 MUTANT CROP HARVESTED! Spawning massive rewards!');
            
            // Play mutant wow sound
            this.sound.play('mutant-wow', { volume: 0.7 });
            
            // Add 10 random crops
            for (let i = 0; i < 10; i++) {
                const cropId = this.getRandomCropId();
                rewards.push({
                    type: 'crop',
                    id: cropId
                });
            }
            
            // Add 10 random seedbags
            for (let i = 0; i < 10; i++) {
                const seedId = this.getRandomSeedId();
                rewards.push({
                    type: 'seedbag',
                    id: seedId
                });
            }
            
            // console.log(`🎉 Mutant rewards: ${rewards.length} items (10 crops + 10 seedbags) with improved spread!`);
            // console.log(`🔥 Mutant crop IDs: ${rewards.filter(r => r.type === 'crop').map(r => r.id).join(', ')}`);
            // console.log(`🌱 Mutant seed IDs: ${rewards.filter(r => r.type === 'seedbag').map(r => r.id).join(', ')}`);
        
        } else {
            // Normal harvest - calculate seedbag rewards
            const seedbagCount = this.calculateSeedbagReward();
            //console.log(`Harvest rewards: ${seedbagCount} seedbags`);
            
            // Add seedbags for this crop type - convert crop ID to seed ID
            const seedId = this.getSeedIdFromCropId(cropId);
            if (seedId) {
                //console.log(`🌱 Crop ID ${cropId} mapped to seed ID ${seedId}`);
                for (let i = 0; i < seedbagCount; i++) {
                    rewards.push({
                        type: 'seedbag',
                        id: seedId // Use correct seed ID for this crop type
                    });
                }
            } else {
                console.warn(`No seed mapping found for crop ID ${cropId}`);
            }
        }
        
        // Spawn rewards with equal circular distribution
        if (rewards.length > 0) {
            // Calculate equal angle spacing: 360 degrees / number of rewards
            const degreesPerReward = Math.floor(360 / rewards.length);
            const angleIncrement = (degreesPerReward * Math.PI) / 180; // Convert to radians
            const startAngle = Math.random() * Math.PI * 2; // Random starting angle for variety
            
           // console.log(`🎯 Spawning ${rewards.length} rewards with ${degreesPerReward}° spacing`);
            
            rewards.forEach((reward, index) => {
                // Calculate the specific angle for this reward
                const rewardAngle = (startAngle + (index * angleIncrement)) % (Math.PI * 2);
                
                // Staggered timing: slower for better pickup and visual appeal
                this.time.delayedCall(200 * index, () => {
                    this.spawnRewardItem(originX, originY, reward, rewardAngle);
                    this.sound.play('seedbag-spawn', { volume: 0.4 });
                });
            });
        }
    }
    
    spawnRewardItem(originX, originY, reward, angle) {
        // Calculate spawn direction using the angle - cut distance in half as requested
        const distance = 22 + Math.random() * 8; // 22-30 pixel spread (was 45-60)
        const targetX = originX + Math.cos(angle) * distance;
        const targetY = originY + Math.sin(angle) * distance;
        
        // Create the reward sprite
        let sprite;
        let textureKey;
        
        if (reward.type === 'crop') {
            const cropItem = itemManager.getItemData(`Consumable_${reward.id}`);
            if (cropItem) {
                textureKey = cropItem.textureKey;
                sprite = this.add.image(originX, originY, textureKey, 0); // Frame 0 for produce
            }
        } else if (reward.type === 'seedbag') {
            const seedItem = itemManager.getItemData(`Seeds_${reward.id}`);
            if (seedItem) {
                textureKey = seedItem.textureKey;
                sprite = this.add.image(originX, originY, textureKey, 1); // Frame 1 for seedbag
            }
        }
        
        if (!sprite) {
            console.warn(`Failed to create reward sprite for ${reward.type} ${reward.id}`);
            return;
        }
        
        sprite.setDepth(2.8); // Same as produce items
        sprite.setOrigin(0.5, 0.5);
        
        // Enhanced arc animation with slight upward trajectory  
        const arcHeight = 8 + Math.random() * 6; // Reduced arc height for better performance
        this.tweens.add({
            targets: sprite,
            x: targetX,
            y: targetY,
            duration: 400 + Math.random() * 200, // Faster duration (400-600ms) for better pickup experience
            ease: 'Cubic.easeOut',
            onUpdate: (tween) => {
                // Create parabolic arc effect
                const progress = tween.progress;
                const arcOffset = Math.sin(progress * Math.PI) * arcHeight;
                sprite.y = originY + (targetY - originY) * progress - arcOffset;
            },
            onComplete: () => {
                // Start floating animation
                this.startRewardFloating(sprite, reward);
                
                // Ensure the item is properly registered for pickup
                //console.log(`✅ Reward item ready for pickup: ${reward.type} ${reward.id} at (${targetX}, ${targetY})`);
                
                // Add a timeout to auto-pickup items that get stuck (prevents unpickupable items)
                this.time.delayedCall(15000, () => { // 15 seconds timeout
                    const produceIndex = this.produceItems.findIndex(p => p.sprite === sprite);
                    if (produceIndex !== -1 && !this.produceItems[produceIndex].picked) {
                        //console.log(`⏰ Auto-picking up stuck reward: ${reward.type} ${reward.id}`);
                        this.pickupProduce(this.produceItems[produceIndex], produceIndex);
                    }
                });
            }
        });
        
        // Store reward data for pickup with complete information
        this.produceItems.push({
            sprite: sprite,
            rewardType: reward.type,
            rewardId: reward.id,
            picked: false,
            position: { x: targetX, y: targetY },
            baseY: targetY,
            textureKey: textureKey
        });
    }
    
    startRewardFloating(sprite, reward) {
        // Gentle floating animation
        this.tweens.add({
            targets: sprite,
            y: sprite.y - 5,
            duration: 1500,
            ease: 'Sine.easeInOut',
            yoyo: true,
            repeat: -1
        });
    }
    
    addMutantSparkleAnimation(plotIndex, farmableRow, tileIndex) {
        const plot = this.farmPlots.find(p => p.index === plotIndex);
        if (!plot || !plot.unlocked) return;
        
        // Calculate world position for sparkle
        const tileY = (farmableRow - 1) * 2;
        const tileX = tileIndex - 1;
        const plotLeft = plot.position.x - 80;
        const plotTop = plot.position.y - 40;
        const worldX = plotLeft + (tileX * 16) + 8;
        const worldY = plotTop + (tileY * 16) + 8;
        
        // Create mutant sparkle sprite
        const sparkle = this.add.sprite(worldX, worldY, 'mutant_sparkle');
        sparkle.setDepth(2.95); // Above regular sparkles
        sparkle.setOrigin(0.5, 0.5);
        sparkle.setAlpha(1.0);
        
        // Play mutant sparkle animation
        sparkle.play('mutant_sparkle');
        
        // Store sparkle data
        this.sparkleSprites.push({
            sprite: sparkle,
            plotIndex: plotIndex,
            farmableRow: farmableRow,
            tileIndex: tileIndex,
            type: 'mutant'
        });
        
        ////console.log(`Added mutant sparkle animation for Plot ${plotIndex}, Row ${farmableRow}, Tile ${tileIndex}`);
    }

}

/* END OF COMPILED CODE */ 